import React, { useCallback, useState, useEffect, useRef } from 'react';
import './Dashboard.css';
import './kioskTheme.css';
import './Dashboard.theme.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { getApiBaseUrl } from './apiBaseUrl';
import { buildAuthHeaders, clearUserSession, compactUserForStorage, fetchUserDataWithAuth } from './authSession';
import {
    isKioskBridgeAvailable,
    getKioskRuntimeConfig,
    generateSanctionLetterPdfInKioskWrapper,
    launchPayuInKioskWrapper,
    requestKioskAdminExit,
    subscribeToKioskSystemEvents
} from './kioskBridge';
import { useLanguage } from './languageContext';
import { getLocalizedName } from './nameLocalization';
import SupportRequestForm from './SupportRequestForm';
import { FullPageLoader, useRouteEntryLoader } from './navigationLoader';
import { COMPANY_NAME_OPTIONS } from './companyNameOptions';
import { AaProgressCard, SalaryGuidanceCard, SalarySlipUploadModal, fetchSalarySlipStatus } from './salaryGuidance';

const formatCurrentOfferReason = (offer) => {
    const explanation = String(offer?.decisionExplanationTitle || offer?.decisionExplanation || '').trim();
    if (explanation) return explanation;
    const ruleCode = String(offer?.ruleCode || '').trim();
    const reason = String(offer?.reason || '').trim();
    return [ruleCode, reason].filter(Boolean).join(': ');
};


const getCreditScoreMeta = (score) => {
    const numericScore = Number(score);

    if (!Number.isFinite(numericScore) || numericScore <= 0) {
        return {
            value: '--',
            label: 'Bureau score unavailable',
            color: '#94a3b8'
        };
    }

    if (numericScore >= 750) {
        return { value: numericScore, label: 'Excellent', color: '#10b981' };
    }

    if (numericScore >= 700) {
        return { value: numericScore, label: 'Good', color: '#16a34a' };
    }

    if (numericScore >= 650) {
        return { value: numericScore, label: 'Fair', color: '#f59e0b' };
    }

    return { value: numericScore, label: 'Needs improvement', color: '#ef4444' };
};

const VisibilityIcon = ({ visible }) => (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
        <path d="M2 12C4.4 7.8 8 5.7 12 5.7S19.6 7.8 22 12c-2.4 4.2-6 6.3-10 6.3S4.4 16.2 2 12Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
        {visible ? null : <path d="M4 4l16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />}
    </svg>
);

const UiIcon = ({ name, size = 22, stroke = 'currentColor' }) => {
    const common = {
        width: size,
        height: size,
        viewBox: '0 0 24 24',
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg',
        stroke,
        strokeWidth: '1.9',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        'aria-hidden': 'true',
        focusable: 'false'
    };

    switch (name) {
        case 'home':
            return <svg {...common}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /><path d="M9.5 21v-6h5v6" /></svg>;
        case 'loans':
            return <svg {...common}><rect x="3" y="6" width="18" height="12" rx="2.5" /><path d="M3 10.5h18" /><path d="M7 14h4" /></svg>;
        case 'payments':
            return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3 10h18" /><path d="M16.5 15h1" /></svg>;
        case 'profile':
            return <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M5 20c.8-3.3 3.6-5 7-5s6.2 1.7 7 5" /></svg>;
        case 'settings':
            return <svg {...common}><circle cx="12" cy="12" r="3.3" /><path d="M19.4 15a1.8 1.8 0 0 0 .36 2l.05.05a2 2 0 1 1-2.83 2.83l-.05-.05a1.8 1.8 0 0 0-2-.36 1.8 1.8 0 0 0-1.1 1.64V21a2 2 0 0 1-4 0v-.07a1.8 1.8 0 0 0-1.1-1.64 1.8 1.8 0 0 0-2 .36l-.05.05a2 2 0 0 1-2.83-2.83l.05-.05a1.8 1.8 0 0 0 .36-2 1.8 1.8 0 0 0-1.64-1.1H3a2 2 0 0 1 0-4h.07a1.8 1.8 0 0 0 1.64-1.1 1.8 1.8 0 0 0-.36-2l-.05-.05A2 2 0 1 1 7.13 3.7l.05.05a1.8 1.8 0 0 0 2 .36A1.8 1.8 0 0 0 10.28 2.5V2a2 2 0 0 1 4 0v.07a1.8 1.8 0 0 0 1.1 1.64 1.8 1.8 0 0 0 2-.36l.05-.05a2 2 0 1 1 2.83 2.83l-.05.05a1.8 1.8 0 0 0-.36 2A1.8 1.8 0 0 0 21.5 10.28H22a2 2 0 0 1 0 4h-.07a1.8 1.8 0 0 0-1.64 1.1Z" /></svg>;
        case 'logout':
            return <svg {...common}><path d="M14 16 19 12 14 8" /><path d="M19 12H9" /><path d="M11 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" /></svg>;
        case 'chevron-left':
            return <svg {...common}><path d="m15 18-6-6 6-6" /></svg>;
        case 'chevron-right':
            return <svg {...common}><path d="m9 18 6-6-6-6" /></svg>;
        case 'pay':
            return <svg {...common}><rect x="3" y="6" width="18" height="12" rx="2.5" /><path d="M3 10.5h18" /><path d="M7.2 15h3.6" /></svg>;
        case 'statement':
            return <svg {...common}><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7Z" /><path d="M14 2v5h5" /><path d="M9 13h6" /><path d="M9 17h6" /></svg>;
        case 'support':
            return <svg {...common}><path d="M4 13a8 8 0 1 1 16 0" /><rect x="3" y="13" width="4" height="6" rx="2" /><rect x="17" y="13" width="4" height="6" rx="2" /><path d="M7 19v1a2 2 0 0 0 2 2h2" /></svg>;
        default:
            return null;
    }
};

const PAYU_POPUP_NAME = 'payuHostedCheckout';
const JOURNEY_COMPLETION_STORAGE_PREFIX = 'loanJourneyCompleted:';
const DASHBOARD_CONGRATS_DISMISSED_PREFIX = 'dashboardCongratsDismissed:';
const DASHBOARD_SIDEBAR_EXPANDED_STORAGE_KEY = 'dashboardSidebarExpanded';
const DASHBOARD_PREVIEW_OFFER_AMOUNT = 100000;
const SALARY_BRACKET_OPTIONS = [
    { value: 'BELOW_20K', label: 'Below 20k' },
    { value: '25K_40K', label: '25k to 40k' },
    { value: '40K_60K', label: '40k to 60k' },
    { value: '60K_80K', label: '60k to 80k' },
    { value: 'ABOVE_80K', label: 'Above 80k' }
];
const REFERENCE_RELATION_OPTIONS = [
    { value: 'Father', labelEn: 'Father', labelHi: 'पिता' },
    { value: 'Mother', labelEn: 'Mother', labelHi: 'माता' },
    { value: 'Brother', labelEn: 'Brother', labelHi: 'भाई' },
    { value: 'Sister', labelEn: 'Sister', labelHi: 'बहन' },
    { value: 'Spouse', labelEn: 'Spouse', labelHi: 'जीवनसाथी' },
    { value: 'Friend', labelEn: 'Friend', labelHi: 'मित्र' },
    { value: 'Colleague', labelEn: 'Colleague', labelHi: 'सहकर्मी' },
    { value: 'Relative', labelEn: 'Relative', labelHi: 'रिश्तेदार' },
    { value: 'Other', labelEn: 'Other', labelHi: 'अन्य' }
];
const getJourneyCompletionStorageKey = (mobile) => {
    const normalizedMobile = String(mobile || '').replace(/\D/g, '').trim();
    return normalizedMobile ? `${JOURNEY_COMPLETION_STORAGE_PREFIX}${normalizedMobile}` : '';
};
const getDashboardCongratsDismissedKey = (mobile) => {
    const normalizedMobile = String(mobile || '').replace(/\D/g, '').trim();
    return normalizedMobile ? `${DASHBOARD_CONGRATS_DISMISSED_PREFIX}${normalizedMobile}` : '';
};

const clearJourneyCompletionStorage = (mobile) => {
    const completionKey = getJourneyCompletionStorageKey(mobile);
    if (!completionKey) return;
    try {
        localStorage.removeItem(completionKey);
    } catch (_error) {
        // Best effort only.
    }
};

const clearLoanProgressRecord = async ({ mobile, userId, panNumber, loanId }) => {
    const payload = {};
    const normalizedMobile = String(mobile || '').trim();
    const normalizedPan = String(panNumber || '').trim();
    const normalizedLoanId = String(loanId || '').trim();

    if (normalizedMobile) payload.mobile = normalizedMobile;
    if (Number.isFinite(Number(userId)) && Number(userId) > 0) payload.userId = Number(userId);
    if (normalizedPan) payload.panNumber = normalizedPan;
    if (normalizedLoanId) payload.loanId = normalizedLoanId;

    if (!Object.keys(payload).length) return false;

    const response = await fetch(`${getApiBaseUrl()}/api/loan-progress/clear`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
        throw new Error(data?.message || data?.error || 'Unable to clear journey progress.')
    }
    return true;
};

const formatAddressDisplayValue = (value) => {
    if (!value) return '--';
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed || '--';
    }
    if (typeof value === 'object') {
        const parts = [
            value.address,
            value.line1,
            value.line2,
            value.city,
            value.district,
            value.state,
            value.pincode,
            value.country
        ]
            .map((item) => String(item || '').trim())
            .filter(Boolean);
        return parts.length ? parts.join(', ') : '--';
    }
    return String(value).trim() || '--';
};

const getAadhaarDisplayValue = (maskedUid, aadhaarLast4) => {
    const safeMaskedUid = String(maskedUid || '').trim();
    if (safeMaskedUid) return safeMaskedUid;

    const safeAadhaarLast4 = String(aadhaarLast4 || '').replace(/\D/g, '').slice(-4);
    return safeAadhaarLast4 ? `XXXX XXXX ${safeAadhaarLast4}` : '--';
};

const getOverpaidAmount = (loan) => {
    const amount = Math.max(0, Number(loan?.overpaidAmount || 0));
    // Anything under a paisa is floating-point residue from the interest split, not money
    // the borrower actually paid twice. The pill and the amount read this same threshold,
    // so a loan can never be labelled overpaid without a figure to show for it.
    return amount >= 0.01 ? Number(amount.toFixed(2)) : 0;
};

const getRepaymentDisplayState = (loan) => {
    const dueAmount = Math.max(0, Number(loan?.dueAmount || 0));
    const paymentStatus = String(loan?.paymentStatus || '').trim().toUpperCase();

    if (paymentStatus === 'OVERPAID' || getOverpaidAmount(loan) > 0) return 'OVERPAID';
    if (paymentStatus === 'PENDING' || dueAmount > 0) return 'PENDING';
    return 'PAID';
};

// Overpaid and closed are not alternatives: an overpaid loan is settled too, so the
// pill has to carry both facts rather than letting "Overpaid" hide the closure.
const isLoanSettled = (loan) => {
    const stage = String(loan?.stage || '').trim().toUpperCase();
    const paymentStatus = String(loan?.paymentStatus || '').trim().toUpperCase();
    return stage === 'CLOSED' || ['PAID', 'OVERPAID', 'CLOSED'].includes(paymentStatus);
};

// An overpayment is money owed back, so it keeps paise rather than following the rounded
// whole-rupee formatting used for sanctioned and repayment figures.
const formatOverpaidAmount = (value) => `₹${Math.max(0, Number(value || 0)).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
})}`;

const formatCurrencyAmount = (value) => `₹${Math.max(0, Number(value || 0)).toLocaleString('en-IN')}`;

const formatDisplayDate = (value) => {
    const raw = String(value || '').trim();
    if (['null', 'undefined', 'nan'].includes(raw.toLowerCase())) return '--';
    if (!raw) return '--';

    const dmyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmyMatch) {
        const [, day, month, year] = dmyMatch;
        const parsedDmy = new Date(Number(year), Number(month) - 1, Number(day));
        if (!Number.isNaN(parsedDmy.getTime())) {
            return parsedDmy.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        }
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }

    return raw;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MIN_REPAYMENT_TENURE_DAYS = 7;

const parseValidDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const normalizeRepaymentLoan = (loan = {}) => {
    const totalPaid = Math.max(0, Number(loan.totalPaid ?? loan.total_paid ?? 0));
    const sanctionedAmount = Math.max(0, Number(loan.sanctionedAmount ?? loan.sanctioned_amount ?? 0));
    const interestAmount = Math.max(0, Number(loan.interestAmount ?? loan.interest_amount ?? loan.totalInterestCharge ?? loan.total_interest_charge ?? 0));
    const totalRepaymentAmount = Math.max(
        0,
        Number(loan.totalRepaymentAmount ?? loan.total_repayment_amount ?? 0),
        sanctionedAmount + interestAmount
    );
    const baseRepaymentDue = Math.max(0, Number(loan.baseRepaymentDue ?? loan.base_repayment_due ?? (totalRepaymentAmount - totalPaid)));
    const collectionDueAmount = Math.max(0, Number(loan.collectionDueAmount ?? loan.collection_due_amount ?? loan.collection_total_outstanding ?? 0));
    const rawDueAmount = loan.dueAmount ?? loan.due_amount ?? (collectionDueAmount || baseRepaymentDue);
    const overpaidAmount = Math.max(0, Number(loan.overpaidAmount ?? loan.overpaid_amount ?? 0));
    const lastPaymentAmount = Math.max(0, Number(loan.lastPaymentAmount ?? loan.last_payment_amount ?? 0));
    const earlyRepaymentInterestDays = Math.max(0, Number(loan.earlyRepaymentInterestDays ?? loan.early_repayment_interest_days ?? 0));
    const minimumInterestDays = Math.max(0, Number(loan.minimumInterestDays ?? loan.minimum_interest_days ?? 0));
    const paymentStatus = String(loan.paymentStatus ?? loan.payment_status ?? '').trim().toUpperCase();
    // A closed/paid loan owes nothing. This path (repayments feed) otherwise
    // re-derives interest-till-today + penalty client-side and would resurface a
    // phantom "Payable Now" (with a Pay button) on a loan already fully paid — even
    // when the backend correctly returned dueAmount 0.
    const loanStage = String(loan.stage ?? loan.loanStage ?? '').trim().toUpperCase();
    const isClosedLoan = loanStage === 'CLOSED'
        || ['PAID', 'OVERPAID', 'CLOSED'].includes(paymentStatus)
        || Boolean(loan.closedAt ?? loan.closed_at);
    const disbursedAt = loan.disbursedAt ?? loan.disbursed_at ?? null;
    const repaymentDate = loan.repaymentDate ?? loan.repayment_date ?? loan.dueDate ?? loan.due_date ?? null;
    const dueDate = loan.dueDate ?? loan.due_date ?? loan.repaymentDate ?? loan.repayment_date ?? null;
    const disbursedDateObj = parseValidDate(disbursedAt);
    const dueDateObj = parseValidDate(dueDate || repaymentDate);
    const explicitTenureDays = Math.max(0, Number(loan.tenureDays ?? loan.tenure_days ?? loan.collectionTenureDays ?? loan.collection_tenure_days ?? 0));
    // +1 for inclusive counting (both disbursal and due date) — aligned with backend
    const derivedTenureDays = (disbursedDateObj && dueDateObj)
        ? Math.max(1, Math.ceil((startOfDay(dueDateObj) - startOfDay(disbursedDateObj)) / MS_PER_DAY) + 1)
        : 0;
    const totalTenureDays = explicitTenureDays || derivedTenureDays || Math.max(earlyRepaymentInterestDays, minimumInterestDays, MIN_REPAYMENT_TENURE_DAYS);
    const elapsedTenureDays = disbursedDateObj
        ? Math.max(0, Math.floor((startOfDay(new Date()) - startOfDay(disbursedDateObj)) / MS_PER_DAY))
        : (Number(loan.elapsedDays ?? 0) || 0);
    const minimumRepaymentDays = Math.max(MIN_REPAYMENT_TENURE_DAYS, minimumInterestDays || 0);
    const repaymentLocked = elapsedTenureDays < minimumRepaymentDays;
    const remainingLockDays = repaymentLocked ? (minimumRepaymentDays - elapsedTenureDays) : 0;

    // Use backend-provided accruedInterest when available (from /api/repayments).
    // Only fall back to client-side derivation for fallback loans built from
    // journey data that don't come from the API.
    const backendAccruedInterest = loan.accruedInterest ?? null;
    let accruedInterest;
    if (isClosedLoan) {
        accruedInterest = 0;
    } else if (backendAccruedInterest != null) {
        accruedInterest = Math.max(0, Number(backendAccruedInterest));
    } else {
        // Client-side fallback for journey-based loans without backend data
        const elapsedInterestDays = Math.max(0, Math.min(elapsedTenureDays, totalTenureDays || elapsedTenureDays));
        const perDayInterest = totalTenureDays > 0 ? (interestAmount / totalTenureDays) : 0;
        accruedInterest = Math.max(0, Math.min(interestAmount, Math.round(perDayInterest * elapsedInterestDays)));
    }

    const penaltyTotal = isClosedLoan
        ? 0
        : Math.max(
            0,
            Math.max(
                Number(loan.penaltyLateInterest ?? loan.penalty_late_interest ?? 0),
                Number(loan.penaltyAmount ?? loan.penalty_amount ?? 0)
            )
        );

    // Trust backend dueAmount when available. Only fall back to client-side
    // derivation for journey-based fallback loans that don't have a backend-
    // computed dueAmount. Previously this always took Math.max(backendDue,
    // clientComputedDue) which could inflate the amount beyond what the backend
    // calculated.
    let dueAmount;
    if (isClosedLoan) {
        dueAmount = 0;
    } else if (rawDueAmount != null && loan.dueAmount != null) {
        // Backend provided an authoritative dueAmount — trust it
        dueAmount = Math.max(0, Number(rawDueAmount));
    } else {
        // Fallback: derive from available data (for journey-based loans)
        const computedDueFromElapsed = Math.max(0, sanctionedAmount + accruedInterest + penaltyTotal - totalPaid);
        dueAmount = Math.max(0, Math.max(Number(rawDueAmount || 0), computedDueFromElapsed));
    }

    return {
        ...loan,
        loanId: loan.loanId ?? loan.loan_id,
        userId: loan.userId ?? loan.user_id ?? null,
        mobile: loan.mobile ?? loan.mobileNumber ?? loan.mobile_number ?? '',
        customerName: loan.customerName ?? loan.customer_name ?? '',
        panNumber: loan.panNumber ?? loan.pan_number ?? null,
        aadhaarLast4: loan.aadhaarLast4 ?? loan.aadhaar_last4 ?? null,
        sanctionedAmount,
        disbursedAmount: Math.max(0, Number(loan.disbursedAmount ?? loan.finalDisbursement ?? loan.final_disbursement ?? sanctionedAmount)),
        totalRepaymentAmount,
        baseRepaymentDue: isClosedLoan ? 0 : baseRepaymentDue,
        collectionDueAmount: isClosedLoan ? 0 : collectionDueAmount,
        penaltyLateInterest: isClosedLoan ? 0 : Math.max(0, Number(loan.penaltyLateInterest ?? loan.penalty_late_interest ?? 0)),
        earlyRepaymentInterestDays,
        minimumInterestDays,
        totalPaid,
        lastPaymentAmount,
        dueAmount,
        overpaidAmount,
        disbursedAt,
        repaymentDate,
        dueDate,
        collectionStatus: loan.collectionStatus ?? loan.collection_status ?? null,
        collectionCaseId: loan.collectionCaseId ?? loan.collection_case_id ?? null,
        bucketCode: loan.bucketCode ?? loan.bucket_code ?? null,
        daysPastDue: Number(loan.daysPastDue ?? loan.days_past_due ?? 0),
        principalOutstanding: Math.max(0, Number(loan.principalOutstanding ?? loan.principal_outstanding ?? 0)),
        interestOutstanding: Math.max(0, Number(loan.interestOutstanding ?? loan.interest_outstanding ?? 0)),
        chargesOutstanding: Math.max(0, Number(loan.chargesOutstanding ?? loan.charges_outstanding ?? 0)),
        interestAmount,
        accruedInterest,
        penaltyAmount: isClosedLoan ? 0 : Math.max(0, Number(loan.penaltyAmount ?? loan.penalty_amount ?? 0)),
        latestPaymentAt: loan.latestPaymentAt ?? loan.lastPaymentAt ?? loan.latest_payment_at ?? loan.last_payment_at ?? null,
        paymentStatus: paymentStatus || (overpaidAmount > 0 ? 'OVERPAID' : dueAmount > 0 ? 'PENDING' : 'PAID'),
        totalTenureDays,
        elapsedTenureDays,
        minimumRepaymentDays,
        repaymentLocked,
        remainingLockDays
    };
};


const isTerminalLoanJourney = (journey) => {
    const stage = String(journey?.stage || journey?.loanStage || '').trim().toUpperCase();
    const paymentStatus = String(journey?.paymentStatus || journey?.payment_status || '').trim().toUpperCase();
    return Boolean(journey?.loanId || journey?.loan_id) && (
        stage === 'CLOSED'
        || ['PAID', 'OVERPAID', 'CLOSED'].includes(paymentStatus)
    );
};

const buildLoanRowFromJourney = (journey) => {
    if (!journey?.loanId) return null;

    const sanctionedAmount = Number(journey.sanctionedAmount || journey.sanctioned_amount || journey.finalDisbursement || journey.final_disbursement || 0);
    const interestAmount = Number(journey.totalInterestCharge || journey.total_interest_charge || 0);
    const totalRepaymentAmount = Math.max(Number(journey.totalRepaymentAmount || journey.total_repayment_amount || 0), sanctionedAmount + interestAmount);
    const repaymentDate = journey.repaymentDate || journey.repayment_date || null;
    const paymentStatus = String(journey.paymentStatus || journey.payment_status || '').trim().toUpperCase();
    const stage = String(journey.stage || '').trim().toUpperCase();
    const isClosed = stage === 'CLOSED' || ['PAID', 'OVERPAID', 'CLOSED'].includes(paymentStatus);
    const dueAmount = isClosed
        ? 0
        : Math.max(0, totalRepaymentAmount || sanctionedAmount);

    return {
        loanId: journey.loanId,
        userId: journey.userId || null,
        mobile: journey.mobileNumber || journey.mobile_number || '',
        email: journey.email || '',
        customerName: journey.customerName || journey.customer_name || '',
        panNumber: journey.panNumber || journey.pan_number || null,
        aadhaarLast4: journey.aadhaarLast4 || journey.aadhaar_last4 || null,
        sanctionedAmount,
        disbursedAmount: Number(journey.finalDisbursement || journey.final_disbursement || sanctionedAmount || 0),
        totalRepaymentAmount,
        baseRepaymentDue: dueAmount,
        collectionDueAmount: 0,
        penaltyLateInterest: 0,
        totalPaid: 0,
        dueAmount,
        overpaidAmount: 0,
        repaymentDate,
        dueDate: repaymentDate,
        stage,
        collectionStatus: stage,
        collectionCaseId: null,
        bucketCode: null,
        daysPastDue: 0,
        principalOutstanding: 0,
        interestOutstanding: 0,
        chargesOutstanding: 0,
        interestRateValue: null,
        interestRateUnit: null,
        interestAmount,
        penaltyRateValue: null,
        penaltyRateUnit: null,
        penaltyAmount: 0,
        createdAt: journey.createdAt || journey.created_at || null,
        latestPaymentAt: journey.latestPaymentAt || journey.last_payment_at || journey.disbursedAt || journey.disbursed_at || null,
        lastPaymentAmount: Number(journey.lastPaymentAmount || journey.last_payment_amount || 0),
        paymentStatus: isClosed ? paymentStatus : (dueAmount > 0 ? 'PENDING' : 'PAID')
    };
};

const buildLoanJourneyFromRepaymentLoan = (loan) => {
    if (!loan?.loanId) return null;

    return {
        loanId: loan.loanId,
        userId: loan.userId || null,
        mobileNumber: loan.mobile || loan.mobileNumber || '',
        email: loan.email || '',
        customerName: loan.customerName || '',
        panNumber: loan.panNumber || null,
        aadhaarLast4: loan.aadhaarLast4 || null,
        sanctionedAmount: loan.sanctionedAmount || loan.totalRepaymentAmount || 0,
        finalDisbursement: loan.disbursedAmount || loan.sanctionedAmount || 0,
        totalInterestCharge: loan.interestAmount || 0,
        totalRepaymentAmount: loan.totalRepaymentAmount || loan.sanctionedAmount || 0,
        repaymentDate: loan.repaymentDate || loan.dueDate || null,
        stage: loan.stage || 'DISBURSED',
        paymentStatus: loan.paymentStatus || 'PENDING',
        createdAt: loan.createdAt || null,
        updatedAt: loan.updatedAt || loan.latestPaymentAt || null
    };
};

const formatDobDisplayValue = (dob) => {
    const raw = String(dob || '').trim();
    if (!raw) return '--';

    const digitOnly = raw.replace(/\D/g, '');
    if (digitOnly.length === 6) {
        const day = digitOnly.slice(0, 2);
        const month = digitOnly.slice(2, 4);
        const year = digitOnly.slice(4, 6);
        return `${day}/${month}/${year}`;
    }

    const ymdMatch = raw.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
    if (ymdMatch) {
        const [, year, month, day] = ymdMatch;
        return `${day}/${month}/${year}`;
    }

    const dmyMatch = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    if (dmyMatch) {
        const [, day, month, year] = dmyMatch;
        return `${day}/${month}/${year}`;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;

    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = String(parsed.getFullYear());
    return `${day}/${month}/${year}`;
};

const getJourneyProgressMeta = ({ user, hasCurrentOffer, loanCount, hasJourneyCompletionFlag, hasPendingJourney }) => {
    const hasMobile = Boolean(String(user?.mobile || '').trim() && String(user?.mobile || '').trim() !== '--');
    const hasPan = Boolean(String(user?.panNumber || '').trim() && String(user?.panNumber || '').trim() !== '--');
    const hasKyc = Boolean(String(user?.aadhaarLast4 || '').trim() || String(user?.aadhaarMaskedUid || '').trim());
    const hasDisbursedLoan = Boolean(hasJourneyCompletionFlag) || Number(loanCount || 0) > 0;

    const journeySteps = [
        { id: 1, title: 'Mobile Verification' },
        { id: 2, title: 'Offer Details' },
        { id: 3, title: 'Account Aggregator' },
        { id: 4, title: 'Loan Details' },
        { id: 5, title: 'Aadhaar E-KYC' },
        { id: 6, title: 'Selfie Verification' },
        { id: 7, title: 'Loan Summary' },
        { id: 8, title: 'Disbursement' }
    ];

    let currentJourneyStep = 1;
    if (hasMobile) currentJourneyStep = 2;
    if (hasPan) currentJourneyStep = 3;
    if (hasKyc) currentJourneyStep = 4;
    if (hasCurrentOffer) currentJourneyStep = 5;
    if (hasDisbursedLoan) currentJourneyStep = journeySteps.length;

    const nextMilestone = hasDisbursedLoan
        ? { title: 'Repayment Active', subtitle: 'Review due amount and repayment status' }
        : hasPendingJourney
            ? { title: 'Journey Pending', subtitle: 'Resume the application to continue disbursement' }
            : hasCurrentOffer
                ? { title: 'Loan Summary', subtitle: 'Choose amount and tenure' }
                : hasKyc
                    ? { title: 'Account Aggregator', subtitle: 'Final BRE eligibility is being prepared' }
                    : { title: 'Complete KYC', subtitle: 'Verify Aadhaar to continue' };

    return {
        currentJourneyStep,
        totalSteps: journeySteps.length,
        journeySteps,
        nextMilestone,
        isJourneyCompleted: hasDisbursedLoan
    };
};

const LATEST_ELIGIBILITY_SUBTITLE = 'Your latest eligibility has been calculated. Continue from the next milestone or review your repayment status.';
const LATEST_ELIGIBILITY_OFFER_COPY = (amount) => `Up to ₹${Number(amount || 0).toLocaleString('en-IN')} is available under the latest eligibility check.`;

const preserveFirstProfilePic = (nextUser) => {
    const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
    const existingProfilePic = String(existingUser.profile_pic_url || '').trim();
    const nextProfilePic = String(nextUser?.profile_pic_url || '').trim();

    if (existingProfilePic && existingProfilePic !== nextProfilePic) {
        return { ...nextUser, profile_pic_url: existingProfilePic };
    }

    return nextUser;
};

function Dashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const { language } = useLanguage();
    const isHindi = language === 'hi';
    const payuPopupRef = useRef(null);
    const companyInputRef = useRef(null);
    const salaryHintModalRef = useRef(null);
    const entryLoaderMessage = useRouteEntryLoader('Opening your dashboard...');
    const [confetti, setConfetti] = useState([]);
    const [showDashboardBanner, setShowDashboardBanner] = useState(true);
    const [user, setUser] = useState(() => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const storedLatestSanctionLetter = storedUser.latest_sanction_letter || null;
        const storedLatestLoanJourney = storedUser.latest_loan_journey || null;
        const storedLatestLoanJourneyIsTerminal = isTerminalLoanJourney(storedLatestLoanJourney);
        const storedLatestLoanJourneyStage = String(storedLatestLoanJourney?.stage || '').trim().toUpperCase();
        const storedHasDisbursedLoan = !storedLatestLoanJourneyIsTerminal && (
            Boolean(storedUser.has_disbursed_loan)
            || storedLatestLoanJourneyStage === 'DISBURSED'
        );
        return {
            id: storedUser.id || null,
            name: storedUser.full_name || 'User',
            mobile: storedUser.mobile_number || '--',
            loanTarget: Number(storedUser.loan_target) || Number(storedUser.marketing_limit) || Number(storedUser.current_offer?.offeredAmount) || Number(storedUser.current_offer?.amount) || 0,
            marketingLimit: Number(storedUser.marketing_limit) || Number(storedUser.loan_target) || Number(storedUser.current_offer?.offeredAmount) || Number(storedUser.current_offer?.amount) || 0,
            currentOfferAmount: Number(storedUser.current_offer?.offeredAmount) || Number(storedUser.current_offer?.amount) || 0,
            currentOfferEligible: Boolean(storedUser.current_offer?.isEligible) || (Number(storedUser.current_offer?.offeredAmount) || Number(storedUser.current_offer?.amount) || 0) > 0,
            currentOfferDecisionStatus: String(storedUser.current_offer?.decisionStatus || '').trim().toUpperCase(),
            currentOfferRuleCode: String(storedUser.current_offer?.ruleCode || '').trim().toUpperCase(),
            brePassApplied: Boolean(storedUser.current_offer?.brePassApplied),
            currentOfferReason: formatCurrentOfferReason(storedUser.current_offer),
            currentOffer: storedUser.current_offer || null,
            aaStatusRaw: storedUser.aa_status || null,
            aaStatusStatus: String(storedUser.aa_status?.status || '').trim().toUpperCase(),
            aaStatusPhase: String(storedUser.aa_status?.phase || '').trim().toUpperCase(),
            profilePic: storedUser.profile_pic_url || null,
            panNumber: storedUser.pan_number || '--',
            dob: storedUser.dob || null,
            joinedAt: storedUser.created_at || null,
            aadhaarLast4: storedUser.aadhaar_last4 || null,
            aadhaarMaskedUid: storedUser.aadhaar_verification?.maskedUid || storedUser.aadhaarMaskedUid || null,
            panAddress: storedUser.pan_address || storedUser.panAddress || storedUser.address || null,
            cibilScore: storedUser.cibil_score ?? null,
            hasDisbursedLoan: storedHasDisbursedLoan,
            latestSanctionLetter: storedLatestSanctionLetter,
            latestLoanJourney: storedLatestLoanJourney,
            hasPendingLoanJourney: Boolean(storedUser.has_pending_loan_journey)
        };
    });

    const refreshDashboardUserData = useCallback(async () => {
        if (!user.mobile || user.mobile === '--') return null;
        try {
            const response = await fetchUserDataWithAuth(user.mobile);
            if (response.status === 401 || response.status === 403 || response.status === 404) {
                clearUserSession();
                navigate('/login', { replace: true });
                return null;
            }
            if (!response.ok) return null;

            const data = await response.json();
            if (!data.user) return null;

            const mergedUser = preserveFirstProfilePic(data.user);
            const mergedLatestLoanJourney = mergedUser.latest_loan_journey || null;
            const mergedLatestLoanJourneyIsTerminal = isTerminalLoanJourney(mergedLatestLoanJourney);
            if (mergedLatestLoanJourneyIsTerminal) {
                clearJourneyCompletionStorage(user.mobile);
                clearLoanProgressRecord({
                    mobile: mergedUser.mobile_number || user.mobile,
                    userId: mergedUser.id || user.id || null,
                    panNumber: mergedUser.pan_number || '',
                    loanId: mergedLatestLoanJourney?.loanId || mergedLatestLoanJourney?.loan_id || ''
                }).catch((error) => {
                    console.error('Failed to clear completed journey progress:', error);
                });
            }
            setUser(prev => {
                const backendOfferAmount = Number(mergedUser.current_offer?.offeredAmount) || 0;
                const backendOfferEligible = Boolean(mergedUser.current_offer?.isEligible) && backendOfferAmount > 0;
                const backendOfferDecisionStatus = String(mergedUser.current_offer?.decisionStatus || '').trim().toUpperCase();
                const nextUserState = {
                    ...prev,
                    name: mergedUser.full_name,
                    profilePic: mergedUser.profile_pic_url,
                    panNumber: mergedUser.pan_number || '--',
                    dob: mergedUser.dob || null,
                    joinedAt: mergedUser.created_at || null,
                    loanTarget: Number(prev.loanTarget) || Number(mergedUser.loan_target) || Number(mergedUser.marketing_limit) || Number(mergedUser.current_offer?.offeredAmount) || 0,
                    marketingLimit: Number(prev.marketingLimit) || Number(mergedUser.marketing_limit) || Number(mergedUser.loan_target) || Number(mergedUser.current_offer?.offeredAmount) || 0,
                    currentOfferAmount: backendOfferAmount,
                    currentOfferEligible: backendOfferEligible,
                    currentOfferDecisionStatus: backendOfferDecisionStatus,
                    currentOfferRuleCode: String(mergedUser.current_offer?.ruleCode || '').trim().toUpperCase(),
                    brePassApplied: Boolean(mergedUser.current_offer?.brePassApplied),
                    currentOfferReason: formatCurrentOfferReason(mergedUser.current_offer),
                    currentOffer: mergedUser.current_offer || prev.currentOffer || null,
                    aaStatusRaw: mergedUser.aa_status || prev.aaStatusRaw || null,
                    aaStatusStatus: String(mergedUser.aa_status?.status || prev.aaStatusStatus || '').trim().toUpperCase(),
                    aaStatusPhase: String(mergedUser.aa_status?.phase || prev.aaStatusPhase || '').trim().toUpperCase(),
                    aadhaarLast4: mergedUser.aadhaar_last4 || null,
                    aadhaarMaskedUid: mergedUser.aadhaar_verification?.maskedUid || mergedUser.aadhaarMaskedUid || prev.aadhaarMaskedUid || null,
                    panAddress: mergedUser.pan_address || mergedUser.panAddress || mergedUser.address || null,
                    cibilScore: mergedUser.cibil_score ?? null,
                    hasDisbursedLoan: (
                        Boolean(mergedUser.has_disbursed_loan)
                        || String(mergedUser.latest_loan_journey?.stage || '').trim().toUpperCase() === 'DISBURSED'
                    ) && !isTerminalLoanJourney(mergedUser.latest_loan_journey),
                    latestSanctionLetter: mergedUser.latest_sanction_letter || null,
                    latestLoanJourney: mergedUser.latest_loan_journey || null,
                    hasPendingLoanJourney: Boolean(mergedUser.has_pending_loan_journey) && !isTerminalLoanJourney(mergedUser.latest_loan_journey)
                };
                localStorage.setItem('user', JSON.stringify(compactUserForStorage(mergedUser)));

                return nextUserState;
            });
            setLoanSignalsHydrated(true);
            return mergedUser;
        } catch (err) {
            console.error("Failed to fetch fresh user data:", err);
            return null;
        }
    }, [navigate, user.mobile, user.id]);

    // Fetch fresh user data on mount to ensure profile pic, AA salary, and offers are up to date.
    useEffect(() => {
        refreshDashboardUserData();
    }, [refreshDashboardUserData]);

    useEffect(() => {
        const offerStatus = String(user.currentOfferDecisionStatus || '').trim().toUpperCase();
        const offerRuleCode = String(user.currentOfferRuleCode || '').trim().toUpperCase();
        const aaStatus = String(user.aaStatusStatus || '').trim().toUpperCase();
        const aaPhase = String(user.aaStatusPhase || '').trim().toUpperCase();
        const aaFlowStarted = aaStatus === 'PENDING' || ['PENDING', 'CONSENT_ACTIVE', 'DATA_PENDING'].includes(aaPhase);
        const shouldPollAaOffer = Boolean(user.mobile && user.mobile !== '--')
            && aaFlowStarted
            && !user.currentOfferEligible
            && (offerStatus === 'PENDING' || offerRuleCode === 'AA_PENDING');

        if (!shouldPollAaOffer) return undefined;

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 18;
        const poll = async () => {
            if (cancelled || attempts >= maxAttempts) return;
            attempts += 1;
            const refreshedUser = await refreshDashboardUserData();
            const refreshedOffer = refreshedUser?.current_offer || null;
            const refreshedOfferAmount = Number(refreshedOffer?.offeredAmount || refreshedOffer?.amount || 0);
            if (Boolean(refreshedOffer?.isEligible || refreshedOffer?.approved) && refreshedOfferAmount > 0) {
                cancelled = true;
            }
        };

        const initialTimer = window.setTimeout(poll, 10000);
        const interval = window.setInterval(poll, 20000);

        return () => {
            cancelled = true;
            window.clearTimeout(initialTimer);
            window.clearInterval(interval);
        };
    }, [
        refreshDashboardUserData,
        user.currentOfferDecisionStatus,
        user.currentOfferEligible,
        user.currentOfferRuleCode,
        user.aaStatusPhase,
        user.aaStatusStatus,
        user.mobile
    ]);

    useEffect(() => {
        const userMobile = String(user.mobile || '').replace(/\D/g, '').slice(-10);
        if (!userMobile || userMobile.length < 10) return undefined;
        let cancelled = false;
        fetchSalarySlipStatus(userMobile)
            .then((status) => { if (!cancelled && status) setSalarySlipStatus(status); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [user.mobile]);

    const [activeTab, setActiveTab] = useState(() => {
        const requestedTab = String(location.state?.tab || '').trim();
        return requestedTab || 'dashboard';
    });
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
        try {
            return localStorage.getItem(DASHBOARD_SIDEBAR_EXPANDED_STORAGE_KEY) === '1';
        } catch (_error) {
            return false;
        }
    });
    const [showCongrats, setShowCongrats] = useState(() => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const mobile = storedUser.mobile_number || '';
        const completionKey = getJourneyCompletionStorageKey(mobile);
        const dismissedKey = getDashboardCongratsDismissedKey(mobile);
        const hasCompletedJourney = Boolean(completionKey && localStorage.getItem(completionKey));
        if (hasCompletedJourney) return false;
        return dismissedKey ? !sessionStorage.getItem(dismissedKey) : true;
    });
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [repaymentLockModal, setRepaymentLockModal] = useState({ show: false, message: '' });
    const [showSalaryHintModal, setShowSalaryHintModal] = useState(false);
    const [showSalarySlipModal, setShowSalarySlipModal] = useState(false);
    const [salarySlipStatus, setSalarySlipStatus] = useState(null);
    const [aaPendingSince, setAaPendingSince] = useState(null);
    const [aaManualRefreshing, setAaManualRefreshing] = useState(false);
    // Set by the guidance card's "link another bank account" button; carried into /apply-loan so the
    // Account Aggregator step starts a fresh consent instead of reusing today's profile.
    const restartAaRequestedRef = useRef(false);
    const [showLowSalaryIneligibleModal, setShowLowSalaryIneligibleModal] = useState(false);
    const [selectedCompanyName, setSelectedCompanyName] = useState('');
    const [companySearchQuery, setCompanySearchQuery] = useState('');
    const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
    const [selectedSalaryBracket, setSelectedSalaryBracket] = useState('');
    const [reference1, setReference1] = useState({ name: '', mobile: '', relation: 'Father' });
    const [reference2, setReference2] = useState({ name: '', mobile: '', relation: 'Mother' });
    const [showCreditScore, setShowCreditScore] = useState(true);
    const [supportToast, setSupportToast] = useState({ show: false, message: '' });
    const [kioskRuntime, setKioskRuntime] = useState({
        isElectron: false,
        adminExitConfigured: false
    });
    const [repaymentState, setRepaymentState] = useState({
        loading: false,
        initiating: '',
        loans: [],
        error: ''
    });
    const [loanSignalsHydrated, setLoanSignalsHydrated] = useState(false);
    const [sanctionDownloadBusy, setSanctionDownloadBusy] = useState(false);
    const [sanctionEmailBusy, setSanctionEmailBusy] = useState(false);
    const [sanctionLetterPreviewDocument, setSanctionLetterPreviewDocument] = useState('');
    const [sanctionLetterPreviewText, setSanctionLetterPreviewText] = useState('');
    const [showSanctionLetterPreviewModal, setShowSanctionLetterPreviewModal] = useState(false);
    const handleCompanySuggestionSelect = useCallback((name) => {
        const resolved = String(name || '').trim();
        if (!resolved) return;
        setSelectedCompanyName(resolved);
        setCompanySearchQuery(resolved);
        if (companyInputRef.current) {
            companyInputRef.current.value = resolved;
            companyInputRef.current.focus();
        }
        setShowCompanySuggestions(false);
    }, []);

    useEffect(() => {
        if (!showSalaryHintModal || !showCompanySuggestions) return undefined;
        const handleOutsideClick = (event) => {
            if (!salaryHintModalRef.current) return;
            if (!salaryHintModalRef.current.contains(event.target)) {
                setShowCompanySuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [showSalaryHintModal, showCompanySuggestions]);

    useEffect(() => {
        const userMobile = String(user.mobile || '').replace(/\D/g, '').slice(-10);
        if (!userMobile || userMobile.length < 10) return;
        let cancelled = false;

        // These values contain customer-specific information. Reset them before loading the
        // current customer's server-side hints so a kiosk session can never display values
        // left in component state by a previous customer.
        setSelectedCompanyName('');
        setCompanySearchQuery('');
        setSelectedSalaryBracket('');
        setReference1({ name: '', mobile: '', relation: 'Father' });
        setReference2({ name: '', mobile: '', relation: 'Mother' });

        fetch(`${getApiBaseUrl()}/api/user-reference-hints?mobile=${encodeURIComponent(userMobile)}`, {
            headers: buildAuthHeaders()
        })
            .then((res) => res.json())
            .then((data) => {
                if (cancelled || !data?.success) return;
                const companyName = String(data.companyName || '').trim();
                setReference1({
                    name: data.reference1?.name || '',
                    mobile: data.reference1?.mobile || '',
                    relation: data.reference1?.relation || 'Father'
                });
                setReference2({
                    name: data.reference2?.name || '',
                    mobile: data.reference2?.mobile || '',
                    relation: data.reference2?.relation || 'Mother'
                });
                setSelectedCompanyName(companyName);
                setCompanySearchQuery(companyName);
                setSelectedSalaryBracket(String(data.salaryBracket || '').trim());
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [user.mobile]);
    const creditScoreMetaBase = getCreditScoreMeta(user.cibilScore);
    const creditScoreMeta = {
        ...creditScoreMetaBase,
        label: isHindi
            ? ({
                'Bureau score unavailable': 'ब्यूरो स्कोर उपलब्ध नहीं',
                Excellent: 'उत्कृष्ट',
                Good: 'अच्छा',
                Fair: 'सामान्य',
                'Needs improvement': 'सुधार आवश्यक'
            }[creditScoreMetaBase.label] || creditScoreMetaBase.label)
            : creditScoreMetaBase.label
    };
    const latestSanctionLetter = loanSignalsHydrated ? (user.latestSanctionLetter || null) : null;
    const latestLoanJourney = loanSignalsHydrated ? (user.latestLoanJourney || null) : null;
    const latestSanctionLetterHtml = String(latestSanctionLetter?.html || '').trim();
    const latestLoanJourneyStage = String(latestLoanJourney?.stage || '').trim().toUpperCase();
    const latestLoanJourneyPaymentStatus = String(latestLoanJourney?.paymentStatus || '').trim().toUpperCase();
    const latestLoanJourneyIsTerminal = Boolean(
        latestLoanJourney?.loanId
        && (
            latestLoanJourneyStage === 'CLOSED'
            || ['PAID', 'OVERPAID', 'CLOSED'].includes(latestLoanJourneyPaymentStatus)
        )
    );
    const hasCurrentLoanJourney = loanSignalsHydrated && Boolean(
        user.hasDisbursedLoan
        || user.hasPendingLoanJourney
        || (latestLoanJourney?.loanId && !latestLoanJourneyIsTerminal)
    );
    const latestLoanJourneyIsDisbursed = latestLoanJourneyStage === 'DISBURSED';
    const fallbackRepaymentLoan = buildLoanRowFromJourney(
        latestLoanJourney?.loanId && (latestLoanJourneyIsDisbursed || latestLoanJourneyIsTerminal)
            ? latestLoanJourney
            : null
    );
    const displayRepaymentLoans = (repaymentState.loans.length
        ? repaymentState.loans
        : (fallbackRepaymentLoan ? [fallbackRepaymentLoan] : []))
        .map(normalizeRepaymentLoan)
        .filter((loan) => loan.loanId);
    const hasDisbursedLoan = loanSignalsHydrated && (Boolean(user.hasDisbursedLoan) || Boolean(repaymentState.loans.length) || latestLoanJourneyIsDisbursed);
    const activeLoanCount = displayRepaymentLoans.length;
    const outstandingLoanCount = displayRepaymentLoans.filter((loan) => Number(loan.dueAmount || 0) > 0).length;
    const hasPendingLoanJourney = loanSignalsHydrated && Boolean(
        user.hasPendingLoanJourney
        || (
            latestLoanJourney
            && latestLoanJourneyStage
            && latestLoanJourneyStage !== 'DISBURSED'
            && !['PAID', 'OVERPAID', 'CLOSED'].includes(latestLoanJourneyPaymentStatus)
        )
    );
    const visibleLoanCount = hasDisbursedLoan
        ? activeLoanCount
        : (hasPendingLoanJourney ? 1 : 0);
    const visibleLoanSubtitle = hasDisbursedLoan
        ? (outstandingLoanCount ? (isHindi ? 'पुनर्भुगतान उपलब्ध' : 'Repayment available') : (isHindi ? 'कोई बकाया नहीं' : 'No outstanding dues'))
        : hasPendingLoanJourney
            ? (isHindi ? 'डिस्बर्समेंट से पहले' : 'Before disbursement')
            : (isHindi ? 'कोई सक्रिय देनदारी नहीं' : 'No active liabilities');
    const sanctionLetterPreviewHtml = latestSanctionLetterHtml;
    const rawSanctionLetterPreviewUrl = String(latestSanctionLetter?.viewUrl || '').trim();
    const sanctionLetterPreviewUrl = /^https?:\/\//i.test(rawSanctionLetterPreviewUrl) || rawSanctionLetterPreviewUrl.startsWith('/')
        ? rawSanctionLetterPreviewUrl
        : null;
    const rawSanctionLetterDownloadUrl = String(latestSanctionLetter?.downloadUrl || '').trim();
    const sanctionLetterDownloadUrl = /^https?:\/\//i.test(rawSanctionLetterDownloadUrl) || rawSanctionLetterDownloadUrl.startsWith('/')
        ? rawSanctionLetterDownloadUrl
        : null;
    const sanitizeSanctionLetterHtmlToText = (html) => {
        const raw = String(html || '').trim();
        if (!raw) return '';
        const withoutScripts = raw.replace(/<script[\s\S]*?<\/script>/gi, ' ');
        const withoutStyles = withoutScripts.replace(/<style[\s\S]*?<\/style>/gi, ' ');
        const textOnly = withoutStyles
            .replace(/<\/(p|div|section|article|tr|li|h\d|table)>/gi, '\n')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .replace(/\s+\n/g, '\n')
            .replace(/\n\s+/g, '\n')
            .replace(/[ \t]{2,}/g, ' ')
            .trim();
        return textOnly;
    };
    useEffect(() => {
        let cancelled = false;
        const setPreviewDocument = (html) => {
            const normalizedHtml = String(html || '').trim();
            if (!cancelled) {
                setSanctionLetterPreviewDocument(normalizedHtml);
                setSanctionLetterPreviewText(sanitizeSanctionLetterHtmlToText(normalizedHtml));
            }
        };

        const loadSanctionLetterIframeHtml = async () => {
            if (activeTab !== 'sanction-letter') return;
            if (!cancelled) {
                setSanctionLetterPreviewDocument('');
                setSanctionLetterPreviewText('');
            }

            if (sanctionLetterPreviewUrl) {
                return;
            }

            if (sanctionLetterPreviewHtml) {
                setPreviewDocument(sanctionLetterPreviewHtml);
                return;
            }

            if (!sanctionLetterPreviewUrl) {
                if (!cancelled) {
                    setSanctionLetterPreviewDocument('');
                    setSanctionLetterPreviewText('');
                }
                return;
            }
        };

        loadSanctionLetterIframeHtml();
        return () => {
            cancelled = true;
        };
    }, [activeTab, hasDisbursedLoan, sanctionLetterPreviewHtml, sanctionLetterPreviewUrl, latestSanctionLetter?.loanId]);

    const resolvedSanctionLetterPreviewDocument = sanctionLetterPreviewDocument || sanctionLetterPreviewHtml;
    const hasRenderableSanctionLetter = Boolean(sanctionLetterPreviewText || resolvedSanctionLetterPreviewDocument || sanctionLetterPreviewUrl);
    const shouldShowSanctionLetterTab = loanSignalsHydrated && hasDisbursedLoan && Boolean(
        latestSanctionLetter?.loanId
        || latestLoanJourney?.loanId
    );
    const hasCurrentOffer = Number(user.currentOfferAmount || 0) > 0 && user.currentOfferEligible;
    const isAaOfferPending = Boolean(user.mobile && user.mobile !== '--')
        && !user.currentOfferEligible
        && (['PENDING', 'CONSENT_ACTIVE', 'DATA_PENDING'].includes(String(user.aaStatusPhase || '')) || String(user.aaStatusStatus || '') === 'PENDING')
        && (String(user.currentOfferDecisionStatus || '') === 'PENDING' || String(user.currentOfferRuleCode || '') === 'AA_PENDING');
    const showOfferGuidance = !user.currentOfferEligible
        && !isAaOfferPending
        && Boolean(user.currentOfferRuleCode || user.currentOfferDecisionStatus);
    useEffect(() => {
        setAaPendingSince((prev) => (isAaOfferPending ? (prev || Date.now()) : null));
    }, [isAaOfferPending]);
    const handleAaManualRefresh = async () => {
        setAaManualRefreshing(true);
        try {
            await refreshDashboardUserData();
        } finally {
            setAaManualRefreshing(false);
        }
    };
    const displayedJourneyEntryAmount = DASHBOARD_PREVIEW_OFFER_AMOUNT;
    const displayedCongratsAmount = DASHBOARD_PREVIEW_OFFER_AMOUNT;
    const journeyCompletionStorageKey = getJourneyCompletionStorageKey(user.mobile);
    const dashboardCongratsDismissedKey = getDashboardCongratsDismissedKey(user.mobile);
    const hasJourneyCompletionFlag = Boolean(
        journeyCompletionStorageKey && localStorage.getItem(journeyCompletionStorageKey)
    );
    const localizedUserName = getLocalizedName(user.name, language) || user.name;
    const localizedFirstName = localizedUserName.split(' ')[0] || localizedUserName;
    const journeyProgressMeta = getJourneyProgressMeta({
        user,
        hasCurrentOffer,
        loanCount: activeLoanCount,
        hasJourneyCompletionFlag,
        hasPendingJourney: hasPendingLoanJourney
    });
    const completedJourneySteps = journeyProgressMeta.isJourneyCompleted
        ? journeyProgressMeta.totalSteps
        : Math.max(0, journeyProgressMeta.currentJourneyStep - 1);
    const profileCompletionPercent = Math.round(
        (completedJourneySteps / Math.max(journeyProgressMeta.totalSteps, 1)) * 100
    );
    const profileCompletionMeta = {
        percentage: profileCompletionPercent,
        helperText: profileCompletionPercent >= 100
            ? (isHindi ? 'प्रोफाइल पूरी हो गई है' : 'Profile is complete')
            : (isHindi
                ? `${journeyProgressMeta.totalSteps} में से ${completedJourneySteps} स्टेप पूरे`
                : `${completedJourneySteps} of ${journeyProgressMeta.totalSteps} steps completed`)
    };
    const handleSanctionPdfDownload = async (event) => {
        if (!sanctionLetterDownloadUrl) return;
        if (!isKioskBridgeAvailable()) return;
        event.preventDefault();
        if (sanctionDownloadBusy) return;
        setSanctionDownloadBusy(true);
        try {
            const kioskPdfResult = await generateSanctionLetterPdfInKioskWrapper({
                viewUrl: sanctionLetterPreviewUrl,
                html: sanctionLetterPreviewUrl ? undefined : (sanctionLetterPreviewHtml || undefined),
                fileName: `loan-sanction-letter-${latestSanctionLetter.loanId || 'loan'}.pdf`
            });

            if (kioskPdfResult?.ok && kioskPdfResult.pdfBase64) {
                const binary = atob(kioskPdfResult.pdfBase64);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i += 1) {
                    bytes[i] = binary.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = kioskPdfResult.fileName || `loan-sanction-letter-${latestSanctionLetter.loanId || 'loan'}.pdf`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                return;
            }

            window.open(sanctionLetterDownloadUrl, '_blank', 'noopener,noreferrer');
        } catch (_error) {
            window.open(sanctionLetterDownloadUrl, '_blank', 'noopener,noreferrer');
        } finally {
            setSanctionDownloadBusy(false);
        }
    };

    const handleSanctionHtmlPreview = (event) => {
        if (!sanctionLetterPreviewUrl && !latestSanctionLetter?.html) {
            return;
        }

        event.preventDefault();
        setShowSanctionLetterPreviewModal(true);
    };

    const handleSendSanctionEmail = async () => {
        const loanId = String(latestSanctionLetter?.loanId || '').trim();
        if (!loanId || sanctionEmailBusy) return;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        let recipientEmail = String(user?.email || '').trim().toLowerCase();
        if (!recipientEmail) {
            const prompted = window.prompt(
                isHindi
                    ? 'कृपया स्वीकृति पत्र भेजने के लिए ईमेल दर्ज करें'
                    : 'Please enter email address to send the sanction letter'
            );
            recipientEmail = String(prompted || '').trim().toLowerCase();
        }
        if (!recipientEmail) {
            setSupportToast({
                show: true,
                message: isHindi ? 'ईमेल आवश्यक है।' : 'Email is required.'
            });
            setTimeout(() => setSupportToast({ show: false, message: '' }), 2500);
            return;
        }
        if (!emailPattern.test(recipientEmail)) {
            setSupportToast({
                show: true,
                message: isHindi ? 'कृपया सही ईमेल दर्ज करें।' : 'Please enter a valid email address.'
            });
            setTimeout(() => setSupportToast({ show: false, message: '' }), 2500);
            return;
        }
        setSanctionEmailBusy(true);
        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/loan-journey/send-sanction-email`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    loanId,
                    mobileNumber: user.mobile || undefined,
                    recipientEmail
                })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data?.success) {
                throw new Error(data?.message || 'Unable to send sanction letter email. Please try again.');
            }
            setSupportToast({
                show: true,
                message: data?.message || 'Sanction letter email sent successfully.'
            });
        } catch (error) {
            setSupportToast({
                show: true,
                message: error?.message || 'Unable to send sanction letter email. Please try again.'
            });
        } finally {
            setSanctionEmailBusy(false);
            setTimeout(() => setSupportToast({ show: false, message: '' }), 3500);
        }
    };

    const sidebarItems = [
        { key: 'dashboard', label: isHindi ? 'डैशबोर्ड' : 'Dashboard', icon: 'home' },
        { key: 'profile', label: isHindi ? 'प्रोफाइल' : 'Profile', icon: 'profile' },
        { key: 'settings', label: isHindi ? 'सेटिंग्स' : 'Settings', icon: 'settings' }
    ];
    if (hasCurrentLoanJourney || hasDisbursedLoan) {
        sidebarItems.splice(1, 0,
            { key: 'loans', label: isHindi ? 'मेरे लोन' : 'My Loans', icon: 'loans' },
            { key: 'repayment', label: isHindi ? 'रीपेमेंट सेवा' : 'Repayment Service', icon: 'payments' }
        );
    }
    if (shouldShowSanctionLetterTab) {
        sidebarItems.splice(2, 0, { key: 'sanction-letter', label: isHindi ? 'स्वीकृति पत्र' : 'Sanction Letter', icon: 'statement' });
    }

    useEffect(() => {
        if (hasJourneyCompletionFlag || activeLoanCount > 0) {
            if (dashboardCongratsDismissedKey) {
                sessionStorage.setItem(dashboardCongratsDismissedKey, '1');
            }
            setShowCongrats(false);
            return;
        }
        if (!hasCurrentOffer) {
            setShowCongrats(false);
            return;
        }
        if (!dashboardCongratsDismissedKey) {
            setShowCongrats(true);
            return;
        }
        setShowCongrats(!sessionStorage.getItem(dashboardCongratsDismissedKey));
    }, [dashboardCongratsDismissedKey, hasJourneyCompletionFlag, activeLoanCount, hasCurrentOffer]);

    useEffect(() => {
        const fetchRepayments = async () => {
            if (!user.id && (!user.mobile || user.mobile === '--')) return;
            setRepaymentState((prev) => ({ ...prev, loading: true, error: '' }));
            try {
                const baseUrl = getApiBaseUrl();
                const query = new URLSearchParams();
                if (user.id) query.set('userId', String(user.id));
                if (user.mobile && user.mobile !== '--') query.set('mobile', user.mobile);
                query.set('_ts', String(Date.now()));
                const response = await fetch(`${baseUrl}/api/repayments?${query.toString()}`, {
                    cache: 'no-store',
                    headers: buildAuthHeaders({
                        'Cache-Control': 'no-cache',
                        Pragma: 'no-cache'
                    })
                });
                const data = await response.json();
                if (!response.ok || !data.success) {
                    throw new Error(data.message || 'Failed to load repayment data');
                }
                setRepaymentState((prev) => ({
                    ...prev,
                    loading: false,
                    loans: Array.isArray(data.loans) ? data.loans : [],
                    error: ''
                }));
                setLoanSignalsHydrated(true);

                const activeLoan = Array.isArray(data.loans)
                    ? data.loans.find((loan) => String(loan?.stage || '').trim().toUpperCase() === 'DISBURSED')
                    : null;
                if (activeLoan) {
                    const nextJourney = buildLoanJourneyFromRepaymentLoan(activeLoan);
                    if (nextJourney) {
                        setUser((prev) => {
                            const nextUserState = {
                                ...prev,
                                hasDisbursedLoan: true,
                                latestLoanJourney: nextJourney,
                                hasPendingLoanJourney: Boolean(prev.hasPendingLoanJourney)
                            };
                            try {
                                const currentStoredUser = JSON.parse(localStorage.getItem('user') || '{}');
                                localStorage.setItem('user', JSON.stringify(compactUserForStorage({
                                    ...currentStoredUser,
                                    has_disbursed_loan: true,
                                    latest_loan_journey: nextJourney
                                })));
                            } catch (_error) {
                                // Best-effort sync only.
                            }
                            return nextUserState;
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to fetch repayment data:', error);
                setRepaymentState((prev) => ({
                    ...prev,
                    loading: false,
                    loans: [],
                    error: error.message || 'Unable to load repayment data'
                }));
                setLoanSignalsHydrated(true);
            }
        };
        fetchRepayments();
    }, [user.id, user.mobile]);

    useEffect(() => {
        let active = true;

        const loadKioskRuntime = async () => {
            const config = await getKioskRuntimeConfig();
            if (!active) return;
            setKioskRuntime({
                isElectron: Boolean(config?.isElectron),
                adminExitConfigured: Boolean(config?.adminExitConfigured)
            });
        };

        loadKioskRuntime();
        const unsubscribe = subscribeToKioskSystemEvents((event) => {
            if (event?.type === 'admin-exit-enabled') {
                setKioskRuntime((prev) => ({ ...prev, adminExitConfigured: true }));
            }
        });

        return () => {
            active = false;
            unsubscribe();
        };
    }, []);

    // Generate confetti particles
    useEffect(() => {

        if (showCongrats) {
            const colors = ['#f59e0b', '#fb923c', '#ef4444', '#ec4899', '#f43f5e', '#8b5cf6'];
            const pieces = Array.from({ length: 50 }).map((_, i) => ({
                id: i,
                left: Math.random() * 100,
                delay: Math.random() * 3,
                duration: 2 + Math.random() * 3,
                color: colors[Math.floor(Math.random() * colors.length)],
                rotate: Math.random() * 360
            }));
            setConfetti(pieces);

            // Auto hide confetti after 6 seconds to save performance
            const timer = setTimeout(() => setConfetti([]), 8000);
            return () => clearTimeout(timer);
        }
    }, [showCongrats]);

    const dismissCongratsPopup = () => {
        if (dashboardCongratsDismissedKey) {
            sessionStorage.setItem(dashboardCongratsDismissedKey, '1');
        }
        setShowCongrats(false);
    };

    const handleApplyLoan = () => {
        // Gate on an OUTSTANDING (unpaid) loan, not just any loan in the repayment
        // feed — a fully paid/closed loan still appears here (with dueAmount 0) so
        // the borrower can see its history, and must not block a new application.
        if (outstandingLoanCount > 0) {
            setActiveTab('repayment');
            setSupportToast({
                show: true,
                message: isHindi
                    ? 'नया लोन लेने से पहले मौजूदा डिस्बर्स्ड लोन का पुनर्भुगतान पूरा करें।'
                    : 'Please complete repayment of your existing disbursed loan before starting another loan journey.'
            });
            return;
        }
        if (dashboardCongratsDismissedKey) {
            sessionStorage.setItem(dashboardCongratsDismissedKey, '1');
        }
        // Anyone already eligible for an offer -- bre_pass override or normal engine decision --
        // has necessarily gone through a decision run where salary was resolved (the engine
        // declines with SALARY_NOT_FOUND otherwise). The backend falls back to that resolved
        // salary from offer_decision_audit_logs when this hint is absent, so re-asking company/
        // salary bracket here is a pointless extra step for anyone who's already approved.
        if (user.currentOfferEligible) {
            const resumeLoanJourneyId = String(latestLoanJourney?.loanId || '').trim();
            navigate('/apply-loan', {
                state: {
                    requestedAmount: displayedCongratsAmount,
                    restartAa: restartAaRequestedRef.current,
                    resumeLoanJourney: hasPendingLoanJourney ? resumeLoanJourneyId : '',
                    salaryCompanyName: '',
                    salaryBracket: ''
                }
            });
            return;
        }
        setShowSalaryHintModal(true);
    };

    const handleProceedLoanJourneyWithSalaryHint = () => {
        const typedCompanyName = String(companyInputRef.current?.value || '').trim();
        const resolvedCompanyName = String(selectedCompanyName || typedCompanyName || companySearchQuery || '').trim();
        if (!resolvedCompanyName || !selectedSalaryBracket) {
            setSupportToast({
                show: true,
                message: isHindi
                    ? 'कंपनी और सैलरी ब्रैकेट चुनें।'
                    : 'Please select company name and salary bracket.'
            });
            return;
        }
        if (selectedSalaryBracket === 'BELOW_20K') {
            setShowSalaryHintModal(false);
            setShowLowSalaryIneligibleModal(true);
            return;
        }

        const ref1Name = String(reference1.name || '').trim();
        const ref1Mobile = String(reference1.mobile || '').replace(/\D/g, '').slice(-10);
        const ref2Name = String(reference2.name || '').trim();
        const ref2Mobile = String(reference2.mobile || '').replace(/\D/g, '').slice(-10);
        const userMobile = String(user.mobile || '').replace(/\D/g, '').slice(-10);

        if (!ref1Name || !ref1Mobile || !ref2Name || !ref2Mobile) {
            setSupportToast({
                show: true,
                message: isHindi
                    ? 'कृपया दोनों रेफरेंस के नाम और 10 अंकों का मोबाइल नंबर भरें।'
                    : 'Please fill names and 10-digit mobile numbers for both reference contacts.'
            });
            return;
        }

        if (ref1Mobile.length !== 10 || !/^[6-9]\d{9}$/.test(ref1Mobile)) {
            setSupportToast({
                show: true,
                message: isHindi
                    ? 'रेफरेंस 1 का मोबाइल नंबर 10 अंकों का मान्य भारतीय नंबर होना चाहिए।'
                    : 'Reference 1 mobile number must be a valid 10-digit Indian mobile number.'
            });
            return;
        }

        if (ref2Mobile.length !== 10 || !/^[6-9]\d{9}$/.test(ref2Mobile)) {
            setSupportToast({
                show: true,
                message: isHindi
                    ? 'रेफरेंस 2 का मोबाइल नंबर 10 अंकों का मान्य भारतीय नंबर होना चाहिए।'
                    : 'Reference 2 mobile number must be a valid 10-digit Indian mobile number.'
            });
            return;
        }

        if (ref1Mobile === ref2Mobile) {
            setSupportToast({
                show: true,
                message: isHindi
                    ? 'रेफरेंस 1 और रेफरेंस 2 के मोबाइल नंबर अलग होने चाहिए।'
                    : 'Reference 1 and Reference 2 mobile numbers must be different.'
            });
            return;
        }

        if ((userMobile && ref1Mobile === userMobile) || (userMobile && ref2Mobile === userMobile)) {
            setSupportToast({
                show: true,
                message: isHindi
                    ? 'रेफरेंस नंबर आपका अपना मोबाइल नंबर नहीं हो सकता।'
                    : 'Reference mobile numbers cannot be your own mobile number.'
            });
            return;
        }

        const cleanedRef1 = {
            name: ref1Name,
            mobile: ref1Mobile,
            relation: reference1.relation || 'Father'
        };
        const cleanedRef2 = {
            name: ref2Name,
            mobile: ref2Mobile,
            relation: reference2.relation || 'Mother'
        };

        fetch(`${getApiBaseUrl()}/api/user-reference-hints`, {
            method: 'POST',
            headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                mobile: userMobile || user.mobile,
                userId: user.id,
                reference1: cleanedRef1,
                reference2: cleanedRef2,
                companyName: resolvedCompanyName,
                salaryBracket: selectedSalaryBracket
            })
        }).catch(() => {});

        const resumeLoanJourneyId = String(latestLoanJourney?.loanId || '').trim();
        setShowSalaryHintModal(false);
        navigate('/apply-loan', {
            state: {
                requestedAmount: displayedCongratsAmount,
                restartAa: restartAaRequestedRef.current,
                resumeLoanJourney: hasPendingLoanJourney ? resumeLoanJourneyId : '',
                salaryCompanyName: resolvedCompanyName,
                salaryBracket: selectedSalaryBracket,
                reference1: cleanedRef1,
                reference2: cleanedRef2
            }
        });
    };

    const handleLinkAnotherAccount = () => {
        restartAaRequestedRef.current = true;
        handleApplyLoan();
    };

    const confirmLogout = useCallback(() => {
        clearUserSession();
        navigate('/login', { replace: true });
    }, [navigate]);

    const handleLogout = () => {
        setShowLogoutConfirm(true);
    };

    const handleKioskExit = async () => {
        const result = await requestKioskAdminExit();
        if (!result?.ok && result?.reason === 'bridge-unavailable') {
            window.alert('Kiosk exit is available only in the Electron wrapper.');
            return;
        }
        if (!result?.ok && result?.reason === 'admin-password-not-configured') {
            window.alert('Set KIOSK_ADMIN_PASSWORD or KIOSK_ADMIN_PASSWORD_SHA256 before using kiosk exit.');
        }
    };

    const handleOpenSupportPopup = () => {
        setShowSupportModal(true);
    };

    const handleSupportSuccess = (message) => {
        setSupportToast({
            show: true,
            message: message || 'Your complaint has been registered.'
        });
        setShowSupportModal(false);
    };

    useEffect(() => {
        if (!supportToast.show) return undefined;
        const timer = window.setTimeout(() => {
            setSupportToast({ show: false, message: '' });
        }, 2200);
        return () => window.clearTimeout(timer);
    }, [supportToast.show]);

    useEffect(() => {
        if (!showLowSalaryIneligibleModal) return undefined;
        const timer = window.setTimeout(() => {
            setShowLowSalaryIneligibleModal(false);
            confirmLogout();
        }, 10000);
        return () => window.clearTimeout(timer);
    }, [showLowSalaryIneligibleModal, confirmLogout]);

    useEffect(() => {
        try {
            localStorage.setItem(DASHBOARD_SIDEBAR_EXPANDED_STORAGE_KEY, isSidebarExpanded ? '1' : '0');
        } catch (_error) {
        }
    }, [isSidebarExpanded]);

    const submitPaymentToPopup = (params) => {
        if (!params?.action) {
            throw new Error('Missing PayU redirect URL');
        }

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = params.action;
        form.target = PAYU_POPUP_NAME;
        form.style.display = 'none';

        Object.entries(params).forEach(([key, value]) => {
            if (key === 'action') return;
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = String(value ?? '');
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const handleInitiatePayment = async (loan) => {
        if (!loan?.loanId) return;
        if (loan.repaymentLocked) {
            setRepaymentLockModal({
                show: true,
                message: isHindi
                    ? `शुरुआत के ${loan.minimumRepaymentDays} दिन तक भुगतान संभव नहीं है। कृपया ${loan.remainingLockDays} दिन बाद फिर प्रयास करें।`
                    : `Payment is not allowed during the first ${loan.minimumRepaymentDays} days. Please try again after ${loan.remainingLockDays} day${loan.remainingLockDays > 1 ? 's' : ''}.`
            });
            return;
        }
        const popupWidth = 520;
        const popupHeight = 760;
        const left = Math.max(0, Math.round((window.screen.width - popupWidth) / 2));
        const top = Math.max(0, Math.round((window.screen.height - popupHeight) / 2));
        const popupFeatures = `popup=yes,width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`;
        let popup = payuPopupRef.current;

        if (!popup || popup.closed) {
            popup = window.open('', PAYU_POPUP_NAME, popupFeatures);
            payuPopupRef.current = popup;
        } else {
            popup.focus();
        }

        if (!popup) {
            setRepaymentState((prev) => ({
                ...prev,
                initiating: '',
                error: 'Popup was blocked. Please allow popups to continue to PayU.'
            }));
            return;
        }

        try {
            popup.document.title = isHindi ? 'PayU पर रीडायरेक्ट हो रहा है' : 'Redirecting to PayU';
            popup.document.body.innerHTML = `<div style="font-family:Inter,sans-serif;padding:24px;text-align:center;color:#0f172a;">${isHindi ? 'PayU भुगतान पेज पर रीडायरेक्ट हो रहा है...' : 'Redirecting to PayU payment page...'}</div>`;
        } catch (_error) {
            // The form target still works even after cross-origin navigation starts.
        }

        setRepaymentState((prev) => ({ ...prev, initiating: loan.loanId, error: '' }));
        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/create-payment`, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    loanId: loan.loanId,
                    userId: user.id || null,
                    mobile: user.mobile || ''
                })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Unable to start payment');
            }

            if (launchPayuInKioskWrapper(data)) {
                if (payuPopupRef.current && !payuPopupRef.current.closed) {
                    payuPopupRef.current.close();
                    payuPopupRef.current = null;
                }
                setRepaymentState((prev) => ({
                    ...prev,
                    initiating: ''
                }));
                return;
            }

            submitPaymentToPopup(data);
            popup.focus();

            setRepaymentState((prev) => ({
                ...prev,
                initiating: ''
            }));
        } catch (error) {
            console.error('Payment initiation failed:', error);
            if (payuPopupRef.current && !payuPopupRef.current.closed) {
                payuPopupRef.current.close();
                payuPopupRef.current = null;
            }
            setRepaymentState((prev) => ({
                ...prev,
                initiating: '',
                error: error.message || 'Unable to start payment'
            }));
        }
    };

    const repaymentSummary = displayRepaymentLoans.reduce((summary, loan) => {
        summary.totalRepayment += Number(loan.totalRepaymentAmount || 0);
        summary.payableNow += Number(loan.dueAmount || 0);
        summary.totalPaid += Number(loan.totalPaid || 0);
        summary.penaltyLateInterest += Number(loan.penaltyLateInterest || 0);
        summary.overpaid += Number(loan.overpaidAmount || 0);
        return summary;
    }, {
        totalRepayment: 0,
        payableNow: 0,
        totalPaid: 0,
        penaltyLateInterest: 0,
        overpaid: 0
    });

    const renderContent = () => {
        switch (activeTab) {
            case 'dashboard':
                    const { nextMilestone } = journeyProgressMeta;

                    return (
                        <>
                            <section className="dashboard-overview-grid">
                                <section className="dashboard-hero dashboard-hero-refined">
                                    <div className="hero-content">
                                        <p className="hero-kicker">Offer Preview</p>
                                        <h1>Up to ₹{displayedCongratsAmount.toLocaleString('en-IN')}</h1>
                                        <p>{LATEST_ELIGIBILITY_SUBTITLE}</p>
                                        <div className="hero-cta-row">
                                            <button className="apply-btn" onClick={handleApplyLoan}>
                                                Continue Journey
                                            </button>
                                            <button className="hero-ghost-btn" onClick={() => setActiveTab('profile')}>
                                                <span>View Profile</span>
                                            </button>
                                        </div>
                                        {/* An approved offer is already final; telling that customer to wait for AA
                                            verification reads as "go run the Account Aggregator again". */}
                                        {!hasCurrentOffer && (
                                            <p style={{ marginTop: '12px', color: '#94a3b8', fontSize: '0.88rem' }}>
                                                Final slider amount will be updated after AA verification and BRE evaluation.
                                            </p>
                                        )}
                                    </div>
                                    <div className="hero-visual-wrap">
                                        <div className="hero-visual">₹</div>
                                        <div className="hero-pill">KYC Status: Verified</div>
                                    </div>
                                </section>
                                {isAaOfferPending && (
                                    <AaProgressCard
                                        aaPhase={user.aaStatusPhase}
                                        isHindi={isHindi}
                                        startedAt={aaPendingSince}
                                        onRefresh={handleAaManualRefresh}
                                        refreshing={aaManualRefreshing}
                                    />
                                )}
                                {showOfferGuidance && (
                                    <SalaryGuidanceCard
                                        offer={user.currentOffer}
                                        fallbackReason={user.currentOfferReason}
                                        isHindi={isHindi}
                                        slipStatus={salarySlipStatus}
                                        onLinkAccount={handleLinkAnotherAccount}
                                        onUploadSlip={() => setShowSalarySlipModal(true)}
                                    />
                                )}
                            </section>

                            <section className="dashboard-summary-bar">
                                <div className="summary-pill-card">
                                    <span className="summary-pill-label">Credit Score</span>
                                    <div className="summary-pill-value-row">
                                        <strong style={{ color: creditScoreMeta.color }}>
                                            {showCreditScore ? creditScoreMeta.value : (creditScoreMeta.value === '--' ? '--' : '•••')}
                                        </strong>
                                        {creditScoreMeta.value !== '--' && (
                                            <button
                                                type="button"
                                                className="summary-visibility-btn"
                                                onClick={() => setShowCreditScore((prev) => !prev)}
                                                aria-label={showCreditScore ? 'Hide credit score' : 'Show credit score'}
                                            >
                                                <VisibilityIcon visible={showCreditScore} />
                                            </button>
                                        )}
                                    </div>
                                    <small>{creditScoreMeta.label}</small>
                                </div>
                                <div className={`summary-pill-card ${profileCompletionMeta.percentage >= 100 ? 'summary-pill-card-success' : ''}`}>
                                    <span className="summary-pill-label">Profile Completion</span>
                                    <strong style={profileCompletionMeta.percentage >= 100 ? { color: '#16a34a' } : undefined}>{profileCompletionMeta.percentage}%</strong>
                                    <small>{profileCompletionMeta.helperText}</small>
                                </div>
                                <div className="summary-pill-card">
                                    <span className="summary-pill-label">{hasDisbursedLoan ? 'Active Loans' : (hasPendingLoanJourney ? (isHindi ? 'यात्रा लंबित' : 'Journey Pending') : (isHindi ? 'सक्रिय ऋण' : 'Active Loans'))}</span>
                                    <strong>{visibleLoanCount}</strong>
                                    <small>{visibleLoanSubtitle}</small>
                                </div>
                                <div className="summary-pill-card">
                                    <span className="summary-pill-label">Next Milestone</span>
                                    <strong>{nextMilestone.title}</strong>
                                    <small>{nextMilestone.subtitle}</small>
                                </div>
                            </section>

                            <section className="dashboard-panel-grid">
                                <div className="dashboard-panel dashboard-panel-actions">
                                    <div className="panel-header">
                                        <div>
                                            <p className="panel-kicker">Quick Access</p>
                                            <h3 className="section-title">Quick Actions</h3>
                                        </div>
                                        <span className="panel-chip">2 shortcuts</span>
                                    </div>
                                    <section className="action-grid">
                                        <div className="action-card action-card-repayment" onClick={() => setActiveTab('repayment')}>
                                            <div className="icon-box" style={{ background: '#ecfdf5', color: '#10b981' }}><UiIcon name="pay" /></div>
                                            <div className="action-info action-info-repayment">
                                                <div className="action-info-head">
                                                    <h4>Repayment Service</h4>
                                                    <span className="action-status-chip">
                                                        {hasDisbursedLoan
                                                            ? (activeLoanCount
                                                                ? (isHindi ? `${activeLoanCount} सक्रिय ऋण` : `${activeLoanCount} Active Loan${activeLoanCount > 1 ? 's' : ''}`)
                                                                : (isHindi ? 'कोई सक्रिय ऋण नहीं' : 'No Active Loan'))
                                                            : hasPendingLoanJourney
                                                                ? (isHindi ? 'यात्रा लंबित' : 'Journey Pending')
                                                                : (isHindi ? 'कोई सक्रिय ऋण नहीं' : 'No Active Loan')}
                                                    </span>
                                                </div>
                                                <p>
                                                    {hasDisbursedLoan
                                                        ? (isHindi ? 'पुनर्भुगतान राशि देखें और PayU होस्टेड चेकआउट पर जारी रखें।' : 'View repayment amount and continue to PayU hosted checkout.')
                                                        : hasPendingLoanJourney
                                                            ? (isHindi ? 'लोन जर्नी अभी पूरी नहीं हुई है। इसे पूरा करने के लिए वापस जारी रखें।' : 'Your loan journey is still pending. Continue it to complete disbursement.')
                                                            : (isHindi ? 'कोई पुनर्भुगतान उपलब्ध नहीं है।' : 'No repayment is available yet.')}
                                                </p>
                                            </div>
                                            <div className="action-arrow" aria-hidden="true">
                                                →
                                            </div>
                                        </div>
                                        <div className="action-card" onClick={handleOpenSupportPopup}>
                                            <div className="icon-box" style={{ background: '#fff7ed', color: '#f97316' }}><UiIcon name="support" /></div>
                                            <div className="action-info">
                                                <h4>Help & Support</h4>
                                                <p>Connect with assistance anytime</p>
                                            </div>
                                        </div>
                                    </section>
                                </div>

                                <div className="dashboard-panel dashboard-panel-insights">
                                    <div className="panel-header">
                                        <div>
                                            <p className="panel-kicker">Account Snapshot</p>
                                            <h3 className="section-title">Today&apos;s Overview</h3>
                                        </div>
                                        <span className="panel-chip panel-chip-success">Ready</span>
                                    </div>
                                    <div className="insight-list">
                                        <div className="insight-item">
                                            <span className="insight-icon insight-icon-success">✓</span>
                                            <div>
                                                <h4>Login verified</h4>
                                                <p>Your mobile OTP and profile details are available in this session.</p>
                                            </div>
                                        </div>
                                        <div className="insight-item">
                                            <span className="insight-icon">₹</span>
                                            <div>
                                                <h4>Current offer</h4>
                                                <p>{LATEST_ELIGIBILITY_OFFER_COPY(displayedCongratsAmount)}</p>
                                            </div>
                                        </div>
                                        <div className="insight-item">
                                            <span className="insight-icon insight-icon-info">{activeLoanCount}</span>
                                            <div>
                                                <h4>Repayment status</h4>
                                                <p>
                                                    {hasDisbursedLoan
                                                        ? (activeLoanCount
                                                            ? (isHindi
                                                                ? `${activeLoanCount} सक्रिय ऋण पुनर्भुगतान के लिए उपलब्ध ${activeLoanCount > 1 ? 'हैं' : 'है'}।`
                                                                : `${activeLoanCount} active loan${activeLoanCount > 1 ? 's are' : ' is'} available for repayment.`)
                                                            : (isHindi
                                                                ? 'फिलहाल कोई सक्रिय देनदारी दिखाई नहीं दे रही है।'
                                                                : 'No active liabilities are currently visible.'))
                                                        : hasPendingLoanJourney
                                                            ? (isHindi
                                                                ? 'लोन जर्नी लंबित है। डिस्बर्समेंट से पहले यह सक्रिय लोन नहीं है।'
                                                                : 'Your loan journey is pending. It is not an active loan until disbursement.')
                                                            : (isHindi
                                                                ? 'फिलहाल कोई सक्रिय देनदारी दिखाई नहीं दे रही है।'
                                                                : 'No active liabilities are currently visible.')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </>
                    );
            case 'loans':
                if (!hasDisbursedLoan) {
                    return (
                        <div className="profile-section">
                            <div className="repayment-empty-card">
                                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🏦</div>
                                <h3>{hasPendingLoanJourney ? (isHindi ? 'लोन जर्नी लंबित' : 'Journey Pending') : (isHindi ? 'कोई लोन उपलब्ध नहीं' : 'No Loans Available')}</h3>
                                <p>
                                    {hasPendingLoanJourney
                                        ? (isHindi
                                            ? 'आपकी लोन जर्नी अभी पूरी नहीं हुई है। डिस्बर्समेंट के बाद यह सेक्शन दिखेगा।'
                                            : 'Your loan journey is still pending. This section will appear after disbursement.')
                                        : (isHindi
                                            ? 'आपका लोन अभी डिस्बर्स नहीं हुआ है। डिस्बर्समेंट के बाद यह सेक्शन दिखेगा।'
                                            : 'Your loan has not been disbursed yet. This section will appear after disbursement.')}
                                </p>
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="profile-section">
                        <div className="profile-header-card repayment-header-card" style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)' }}>
                            <div className="profile-avatar" style={{ background: 'rgba(255,255,255,0.2)', fontSize: '2.5rem' }}>💰</div>
                            <div className="profile-header-info repayment-header-info">
                                <div className="repayment-header-top">
                                    <h2>{isHindi ? 'मेरे लोन' : 'My Loans'}</h2>
                                    <span className="kyc-badge repayment-badge" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                                        {isHindi
                                            ? `${activeLoanCount} सक्रिय लोन`
                                            : `${activeLoanCount} Active Loan${activeLoanCount > 1 ? 's' : ''}`}
                                    </span>
                                </div>
                                <p className="repayment-header-copy" style={{ color: 'rgba(255,255,255,0.7)', margin: '6px 0 0', fontSize: '0.9rem' }}>
                                    {activeLoanCount
                                        ? (isHindi ? 'पुनर्भुगतान-योग्य लोन नीचे दिए गए हैं' : 'Repayment-ready loans are listed below')
                                        : (isHindi ? 'कोई बकाया देनदारी नहीं' : 'No outstanding liabilities')}
                                </p>
                            </div>
                        </div>

                        {displayRepaymentLoans.length ? (
                            <div className="repayment-loan-grid">
                                {displayRepaymentLoans.map((loan) => (
                                    <div key={loan.loanId} className="repayment-loan-card">
                                        <div className="repayment-loan-head">
                                            <div>
                                                <h3>{loan.loanId}</h3>
                                                <p>{getLocalizedName(loan.customerName || user.name, language) || (loan.customerName || user.name)}</p>
                                            </div>
                                            <span className={`repayment-status-pill ${getRepaymentDisplayState(loan) === 'PENDING' ? 'pending' : getRepaymentDisplayState(loan) === 'OVERPAID' ? 'overpaid' : 'paid'}`}>
                                                {getRepaymentDisplayState(loan) === 'PENDING'
                                                    ? (isHindi ? 'सक्रिय' : 'Active')
                                                    : getRepaymentDisplayState(loan) === 'OVERPAID'
                                                        ? (isLoanSettled(loan)
                                                            ? (isHindi ? 'बंद · अधिक भुगतान' : 'Closed · Overpaid')
                                                            : (isHindi ? 'अधिक भुगतान' : 'Overpaid'))
                                                        : (isHindi ? 'बंद' : 'Closed')}
                                            </span>
                                        </div>
                                        <div className="repayment-loan-metrics">
                                            <div>
                                                <span>{isHindi ? 'स्वीकृत राशि' : 'Sanctioned'}</span>
                                                <strong>₹{Number(loan.sanctionedAmount || 0).toLocaleString('en-IN')}</strong>
                                            </div>
                                            <div>
                                                <span>{isHindi ? 'कुल पुनर्भुगतान' : 'Total Repayment'}</span>
                                                <strong>₹{Number(loan.totalRepaymentAmount || 0).toLocaleString('en-IN')}</strong>
                                            </div>
                                            <div>
                                                <span>{isHindi ? 'अब तक चुकाया' : 'Paid Till Now'}</span>
                                                <strong>₹{Number(loan.totalPaid || 0).toLocaleString('en-IN')}</strong>
                                            </div>
                                            <div>
                                                <span>{isHindi ? 'पुनर्भुगतान तिथि' : 'Repayment Date'}</span>
                                                <strong>{loan.repaymentDate || '--'}</strong>
                                            </div>
                                            {getOverpaidAmount(loan) > 0 && (
                                                <div>
                                                    <span>{isHindi ? 'अधिक भुगतान राशि' : 'Overpaid Amount'}</span>
                                                    <strong>{formatOverpaidAmount(getOverpaidAmount(loan))}</strong>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ background: 'white', border: '2px dashed #e2e8f0', borderRadius: '20px', padding: '50px 30px', textAlign: 'center' }}>
                                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🏦</div>
                                <h3 style={{ color: '#1e293b', fontSize: '1.4rem', marginBottom: '10px' }}>{isHindi ? 'कोई सक्रिय लोन नहीं' : 'No Active Loans'}</h3>
                                <p style={{ color: '#64748b', marginBottom: '28px', lineHeight: '1.6' }}>
                                    {isHindi
                                        ? 'अभी आपके पास कोई सक्रिय लोन नहीं है।'
                                        : "You don't have any active loans right now."}
                                    <br />
                                    {isHindi
                                        ? 'लाइव ऑफर उपलब्ध होने पर आप यहाँ से आगे बढ़ सकते हैं।'
                                        : 'Once a live offer is available, you can continue from here.'}
                                </p>
                                <button className="primary-btn" onClick={() => setActiveTab('dashboard')} style={{ padding: '14px 40px', fontSize: '1rem' }}>
                                    {isHindi ? 'अभी आवेदन करें →' : 'Apply Now →'}
                                </button>
                            </div>
                        )}

                        <div className="profile-stats-row">
                            <div className="profile-stat">
                                <div className="profile-stat-value" style={{ color: '#3b82f6' }}>{displayRepaymentLoans.length}</div>
                                <div className="profile-stat-label">{isHindi ? 'कुल लोन' : 'Total Loans'}</div>
                                <div className="profile-stat-sub">{isHindi ? 'लाइफटाइम' : 'Lifetime'}</div>
                            </div>
                            <div className="profile-stat-divider" />
                            <div className="profile-stat">
                                <div className="profile-stat-value" style={{ color: '#10b981' }}>
                                    ₹{displayRepaymentLoans.reduce((sum, loan) => sum + Math.max(0, Number(loan.totalRepaymentAmount || 0) - Number(loan.dueAmount || 0)), 0).toLocaleString('en-IN')}
                                </div>
                                <div className="profile-stat-label">{isHindi ? 'चुकाई गई राशि' : 'Amount Repaid'}</div>
                                <div className="profile-stat-sub">{isHindi ? 'दिख रहे लोन' : 'Visible loans'}</div>
                            </div>
                            <div className="profile-stat-divider" />
                            <div className="profile-stat">
                                <div className="profile-stat-value" style={{ color: '#ea580c' }}>
                                    {`Up to ₹${displayedCongratsAmount.toLocaleString('en-IN')}`}
                                </div>
                                <div className="profile-stat-label">{isHindi ? 'ऑफर पूर्वावलोकन' : 'Offer Preview'}</div>
                                <div className="profile-stat-sub">{isHindi ? 'AA के बाद फाइनल' : 'Final after AA'}</div>
                            </div>
                        </div>
                    </div>
                );
            case 'repayment':
                if (!hasDisbursedLoan) {
                    return (
                        <div className="profile-section">
                            <div className="repayment-empty-card">
                                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>💳</div>
                                <h3>{hasPendingLoanJourney ? (isHindi ? 'रीपेमेंट लंबित' : 'Repayment Pending') : (isHindi ? 'कोई रीपेमेंट उपलब्ध नहीं' : 'No Repayment Available')}</h3>
                                <p>
                                    {hasPendingLoanJourney
                                        ? (isHindi
                                            ? 'रीपेमेंट तभी दिखेगा जब लोन डिस्बर्स हो जाएगा।'
                                            : 'Repayment will appear only after the loan is disbursed.')
                                        : (isHindi
                                            ? 'डिस्बर्समेंट के बाद ही रीपेमेंट सेवा दिखेगी।'
                                            : 'Repayment service will appear only after disbursement.')}
                                </p>
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="profile-section">
                        <div className="profile-header-card repayment-header-card">
                            <div className="profile-avatar repayment-avatar">
                                <UiIcon name="payments" size={34} stroke="currentColor" />
                            </div>
                            <div className="profile-header-info repayment-header-info">
                                <div className="repayment-header-top">
                                    <h2>{isHindi ? 'रीपेमेंट सेवा' : 'Repayment Service'}</h2>
                                    <span className="kyc-badge repayment-badge">
                                        {activeLoanCount
                                            ? (isHindi ? `${activeLoanCount} सक्रिय ऋण` : `${activeLoanCount} Active Loan${activeLoanCount > 1 ? 's' : ''}`)
                                            : (isHindi ? 'कोई बकाया नहीं' : 'No Dues Pending')}
                                    </span>
                                </div>
                                <p className="repayment-header-copy">
                                    {isHindi ? 'पुनर्भुगतान राशि देखें और PayU होस्टेड चेकआउट पर जारी रखें।' : 'View repayment amount and continue to PayU hosted checkout.'}
                                </p>
                            </div>
                        </div>

                        <div className="profile-stats-row repayment-stats-row">
                            <div className="profile-stat">
                                <div className="profile-stat-value repayment-stat-total">{formatCurrencyAmount(repaymentSummary.totalRepayment)}</div>
                                <div className="profile-stat-label">{isHindi ? 'कुल पुनर्भुगतान बुक' : 'Total Repayment Book'}</div>
                                <div className="profile-stat-sub">{isHindi ? 'दिख रहे लोन में' : 'Across visible loans'}</div>
                            </div>
                            <div className="profile-stat-divider" />
                            <div className="profile-stat">
                                <div className="profile-stat-value repayment-stat-payable">{formatCurrencyAmount(repaymentSummary.payableNow)}</div>
                                <div className="profile-stat-label">{isHindi ? 'अभी देय भुगतान' : 'Payable Now'}</div>
                                <div className="profile-stat-sub">{isHindi ? 'कलेक्शन/पेनल्टी सहित' : 'Includes collection / penalty if any'}</div>
                            </div>
                            <div className="profile-stat-divider" />
                            <div className="profile-stat">
                                <div className="profile-stat-value repayment-stat-count">{activeLoanCount}</div>
                                <div className="profile-stat-label">{isHindi ? 'लोन उपलब्ध' : 'Loans Available'}</div>
                                <div className="profile-stat-sub">{isHindi ? 'होस्टेड चेकआउट सक्षम' : 'Hosted checkout enabled'}</div>
                            </div>
                        </div>

                        {repaymentState.error && (
                            <div className="repayment-alert">{repaymentState.error}</div>
                        )}

                        {repaymentState.loading ? (
                            <div className="repayment-empty-card">
                                <div style={{ fontSize: '2.6rem', marginBottom: '12px' }}>⏳</div>
                                <h3>{isHindi ? 'पुनर्भुगतान विवरण लोड हो रहा है' : 'Loading repayment details'}</h3>
                                <p>{isHindi ? 'कृपया प्रतीक्षा करें, हम आपके लोन और देय राशि ला रहे हैं।' : 'Please wait while we fetch your loans and payable amount.'}</p>
                            </div>
                        ) : displayRepaymentLoans.length ? (
                            <div className="repayment-loan-grid">
                                {displayRepaymentLoans.map((loan) => (
                                    <div key={loan.loanId} className="repayment-loan-card">
                                        <div className="repayment-loan-head">
                                            <div>
                                                <h3>{loan.loanId}</h3>
                                                <p>{getLocalizedName(loan.customerName || user.name, language) || (loan.customerName || user.name)}</p>
                                            </div>
                                            <span className={`repayment-status-pill ${loan.repaymentLocked ? 'locked' : (getRepaymentDisplayState(loan) === 'PENDING' ? 'pending' : getRepaymentDisplayState(loan) === 'OVERPAID' ? 'overpaid' : 'paid')}`}>
                                                {loan.repaymentLocked
                                                    ? (isHindi ? 'लॉक्ड' : 'Locked')
                                                    : getRepaymentDisplayState(loan) === 'PENDING'
                                                    ? (isHindi ? 'अभी देय' : 'Due now')
                                                    : getRepaymentDisplayState(loan) === 'OVERPAID'
                                                        ? (isLoanSettled(loan)
                                                            ? (isHindi ? 'बंद · अधिक भुगतान' : 'Closed · Overpaid')
                                                            : (isHindi ? 'अधिक भुगतान' : 'Overpaid'))
                                                        : (isHindi ? 'भुगतान हो चुका' : 'Paid')}
                                            </span>
                                        </div>
                                        <div className="repayment-due-strip">
                                            <div>
                                                <span>{isHindi ? 'अभी देय' : 'Payable now'}</span>
                                                <strong>{formatCurrencyAmount(loan.dueAmount)}</strong>
                                            </div>
                                            <button
                                                className={`primary-btn repayment-pay-btn ${loan.repaymentLocked ? 'repayment-pay-btn-locked' : ''}`}
                                                disabled={(!loan.repaymentLocked && Number(loan.dueAmount || 0) <= 0) || repaymentState.initiating === loan.loanId}
                                                onClick={() => handleInitiatePayment(loan)}
                                            >
                                                {repaymentState.initiating === loan.loanId
                                                    ? (isHindi ? 'रीडायरेक्ट हो रहा है...' : 'Redirecting...')
                                                    : loan.repaymentLocked
                                                        ? (isHindi
                                                            ? `भुगतान ${loan.remainingLockDays} दिन बाद`
                                                            : `Pay in ${loan.remainingLockDays} day${loan.remainingLockDays > 1 ? 's' : ''}`)
                                                        : (isHindi
                                                        ? `अभी भुगतान करें ${formatCurrencyAmount(loan.dueAmount)}`
                                                        : `Pay Now ${formatCurrencyAmount(loan.dueAmount)}`)}
                                            </button>
                                        </div>
                                        {loan.repaymentLocked && (
                                            <p className="repayment-payment-hint" style={{ marginTop: '8px' }}>
                                                {isHindi
                                                    ? `भुगतान डिस्बर्सल के ${loan.minimumRepaymentDays} दिन बाद ही संभव है।`
                                                    : `Payment is enabled only after ${loan.minimumRepaymentDays} days from disbursal.`}
                                            </p>
                                        )}
                                        <div className="repayment-loan-metrics">
                                            <div className="repayment-metric-card primary">
                                                <span>{isHindi ? 'स्वीकृत राशि' : 'Sanctioned Amount'}</span>
                                                <strong>{formatCurrencyAmount(loan.sanctionedAmount)}</strong>
                                            </div>
                                            <div className="repayment-metric-card">
                                                <span>{isHindi ? 'डिस्बर्स्ड राशि' : 'Disbursed Amount'}</span>
                                                <strong>{formatCurrencyAmount(loan.disbursedAmount)}</strong>
                                            </div>
                                            <div className="repayment-metric-card">
                                                <span>{isHindi ? 'डिस्बर्स्ड तारीख' : 'Disbursed Date'}</span>
                                                <strong>{formatDisplayDate(loan.disbursedAt)}</strong>
                                            </div>
                                            <div className="repayment-metric-card">
                                                <span>
                                                    {isHindi
                                                        ? `आज तक का ब्याज`
                                                        : `Interest till today`}
                                                </span>
                                                <strong>{formatCurrencyAmount(loan.accruedInterest ?? 0)}</strong>
                                            </div>
                                            <div className="repayment-metric-card">
                                                <span>{isHindi ? 'पेनल्टी' : 'Penalty'}</span>
                                                <strong>{formatCurrencyAmount(loan.penaltyLateInterest)}</strong>
                                            </div>
                                            <div className="repayment-metric-card">
                                                <span>{isHindi ? 'पुनर्भुगतान तिथि' : 'Repayment Date'}</span>
                                                <strong>{formatDisplayDate(loan.repaymentDate)}</strong>
                                            </div>
                                            <div className="repayment-metric-card">
                                                <span>{isHindi ? 'अंतिम भुगतान राशि' : 'Last Payment Amount'}</span>
                                                <strong>{formatCurrencyAmount(loan.lastPaymentAmount)}</strong>
                                            </div>
                                            {getOverpaidAmount(loan) > 0 && (
                                                <div className="repayment-metric-card">
                                                    <span>{isHindi ? 'अधिक भुगतान राशि' : 'Overpaid Amount'}</span>
                                                    <strong>{formatOverpaidAmount(getOverpaidAmount(loan))}</strong>
                                                </div>
                                            )}
                                        </div>
                                        <div className="repayment-loan-footer">
                                            <div className="repayment-loan-footer-meta">
                                                <span className="repayment-footer-label">{isHindi ? 'स्थिति' : 'Stage'}</span>
                                                <strong>
                                                    {isHindi
                                                        ? ({
                                                            SUMMARY_CONFIRMED: 'सारांश पुष्ट',
                                                            SUMMARY_READY: 'सारांश तैयार',
                                                            DISBURSED: 'वितरित',
                                                            CLOSED: 'बंद'
                                                        }[String(loan.stage || '').toUpperCase()] || (loan.stage || '--'))
                                                        : (loan.stage || '--')}
                                                </strong>
                                            </div>
                                            <p className="repayment-payment-hint">
                                                {isHindi
                                                    ? 'देय राशि में आज तक का ब्याज और लागू पेनल्टी शामिल है।'
                                                    : 'Payable amount includes interest accrued till today and applicable penalty.'}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="repayment-empty-card">
                                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📋</div>
                                <h3>{isHindi ? 'कोई पुनर्भुगतान लोन उपलब्ध नहीं' : 'No repayment loans available'}</h3>
                                <p>{isHindi ? 'जैसे ही डिस्बर्सल-रेडी जर्नी उपलब्ध होगी, आपके लोन का विवरण यहाँ दिखेगा।' : 'Your loan details will appear here once a disbursal-ready journey is available.'}</p>
                            </div>
                        )}
                    </div>
                );
            case 'sanction-letter':
                if (!latestSanctionLetter?.loanId) {
                    return (
                        <div className="profile-section">
                            <div className="repayment-empty-card">
                                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📄</div>
                                <h3>{isHindi ? 'स्वीकृति पत्र उपलब्ध नहीं' : 'No Sanction Letter Available'}</h3>
                                <p>
                                    {isHindi
                                        ? 'आपका स्वीकृति पत्र अभी तक तैयार नहीं हुआ है।'
                                        : 'Your sanction letter is not available yet.'}
                                </p>
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="profile-section">
                        <div className="profile-header-card repayment-header-card" style={{ background: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 100%)' }}>
                            <div className="profile-avatar repayment-avatar" style={{ background: 'rgba(255,255,255,0.2)', fontSize: '2.5rem' }}>📄</div>
                            <div className="profile-header-info repayment-header-info">
                                <div className="repayment-header-top">
                                    <h2>{isHindi ? 'स्वीकृति पत्र' : 'Sanction Letter'}</h2>
                                    <span className="kyc-badge repayment-badge" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                                        {latestSanctionLetter?.loanId ? latestSanctionLetter.loanId : (isHindi ? 'कोई पत्र नहीं' : 'No Letter')}
                                    </span>
                                </div>
                                <p className="repayment-header-copy" style={{ color: 'rgba(255,255,255,0.75)', margin: '6px 0 0', fontSize: '0.9rem' }}>
                                    {isHindi
                                        ? 'यह लाइव स्वीकृति पत्र प्रिव्यू है।'
                                        : 'This is the live sanction letter preview rendered from the backend.'}
                                </p>
                            </div>
                        </div>

                        {hasRenderableSanctionLetter ? (
                            <div className="dashboard-panel" style={{ padding: '18px', background: '#fff' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <div>
                                        <h3 style={{ margin: 0, color: '#0f172a' }}>{isHindi ? 'प्रिव्यू' : 'Preview'}</h3>
                                        <p style={{ margin: '6px 0 0', color: '#64748b' }}>
                                            {latestSanctionLetter.loanId ? `Loan ID: ${latestSanctionLetter.loanId}` : ''}
                                        </p>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                        {(sanctionLetterPreviewUrl || latestSanctionLetter.html) && (
                                            <button
                                                className="primary-btn"
                                                type="button"
                                                onClick={handleSanctionHtmlPreview}
                                            >
                                                {isHindi ? 'स्वीकृति पत्र देखें' : 'Preview Sanction Letter'}
                                            </button>
                                        )}
                                        {latestSanctionLetter?.loanId && (
                                            <button
                                                className="hero-ghost-btn"
                                                type="button"
                                                onClick={handleSendSanctionEmail}
                                                disabled={sanctionEmailBusy}
                                                aria-busy={sanctionEmailBusy ? 'true' : 'false'}
                                            >
                                                {sanctionEmailBusy
                                                    ? (isHindi ? 'भेजा जा रहा है…' : 'Sending…')
                                                    : (isHindi ? 'ईमेल भेजें' : 'Send Sanction Letter')}
                                            </button>
                                        )}
                                        {sanctionLetterDownloadUrl && (
                                            <a
                                                href={sanctionLetterDownloadUrl}
                                                className="hero-ghost-btn"
                                                onClick={handleSanctionPdfDownload}
                                                aria-busy={sanctionDownloadBusy ? 'true' : 'false'}
                                            >
                                                {sanctionDownloadBusy
                                                    ? (isHindi ? 'बन रहा है…' : 'Preparing…')
                                                    : (isHindi ? 'PDF डाउनलोड' : 'Download PDF')}
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <div
                                    title="Sanction Letter Preview"
                                    style={{
                                        width: '100%',
                                        minHeight: '1200px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '18px',
                                        background: '#fff',
                                        padding: '20px',
                                        overflow: 'auto'
                                    }}
                                >
                                    {sanctionLetterPreviewUrl ? (
                                        <iframe
                                            title="Sanction Letter Preview"
                                            style={{
                                                width: '100%',
                                                minHeight: '1120px',
                                                border: '0',
                                                background: '#fff'
                                            }}
                                            src={sanctionLetterPreviewUrl}
                                        />
                                    ) : resolvedSanctionLetterPreviewDocument ? (
                                        <iframe
                                            title="Sanction Letter Preview"
                                            style={{
                                                width: '100%',
                                                minHeight: '1120px',
                                                border: '0',
                                                background: '#fff'
                                            }}
                                            srcDoc={resolvedSanctionLetterPreviewDocument || undefined}
                                        />
                                    ) : sanctionLetterPreviewText ? (
                                        <pre style={{
                                            margin: 0,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontFamily: 'Georgia, "Times New Roman", serif',
                                            fontSize: '1rem',
                                            lineHeight: 1.75,
                                            color: '#0f172a'
                                        }}>
                                            {sanctionLetterPreviewText}
                                        </pre>
                                    ) : (
                                        <div style={{ padding: '24px', color: '#64748b' }}>
                                            {isHindi ? 'प्रिव्यू लोड हो रहा है…' : 'Loading preview…'}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="repayment-empty-card">
                                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>📄</div>
                                <h3>{isHindi ? 'स्वीकृति पत्र उपलब्ध नहीं' : 'No Sanction Letter Available'}</h3>
                                <p>
                                    {isHindi
                                        ? 'जब आपकी स्वीकृति पत्र जर्नी तैयार होगी, यह यहाँ दिखाई देगा।'
                                        : 'Your sanction letter will appear here once it is generated and stored.'}
                                </p>
                            </div>
                        )}
                    </div>
                );
            case 'profile':
                return (
                    <div className="profile-section">
                        {/* Profile Header Card - Restructured for Large Image */}
                        <div className="profile-header-card" style={{
                            flexDirection: 'column',
                            textAlign: 'center',
                            padding: '60px 20px',
                            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                            borderRadius: '30px',
                            border: '1px solid rgba(255,255,255,0.05)'
                        }}>
                            <div className="profile-avatar" style={{
                                width: '220px',
                                height: '220px',
                                margin: '0 auto 25px',
                                border: '6px solid rgba(255,255,255,0.1)',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                                borderRadius: '24px' // Changed from circle to square box
                            }}>
                                {user.profilePic ? (
                                    <img src={user.profilePic} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '18px', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ fontSize: '4rem' }}>{(localizedUserName || 'U').split(' ').map(n => n[0]).join('')}</div>
                                )}
                            </div>

                            <div className="profile-header-info" style={{ marginLeft: 0 }}>
                                <h2 style={{ fontSize: '2.4rem', color: 'white', marginBottom: '8px' }}>{localizedUserName}</h2>
                                <span className="kyc-badge" style={{ fontSize: '1rem', padding: '6px 16px' }}>
                                    {isHindi ? '✓ केवाईसी सत्यापित' : '✓ KYC Verified'}
                                </span>
                                <p style={{ color: 'rgba(255,255,255,0.5)', margin: '15px 0 0', fontSize: '1rem' }}>
                                    {isHindi ? 'सदस्यता शुरू: ' : 'Member since '}
                                    {user.joinedAt
                                        ? new Date(user.joinedAt).toLocaleString(isHindi ? 'hi-IN' : 'en-IN', { month: 'short', year: 'numeric' })
                                        : (isHindi ? 'उपलब्ध नहीं' : 'N/A')}
                                </p>
                            </div>
                        </div>

                        {/* Info Grid */}
                        <div className="profile-info-grid">
                            <div className="profile-info-card">
                                <span className="info-label">{isHindi ? '👤 नाम' : '👤 Name'}</span>
                                <span className="info-value">{localizedUserName || user.name || '--'}</span>
                            </div>
                            <div className="profile-info-card">
                                <span className="info-label">{isHindi ? '📱 मोबाइल नंबर' : '📱 Mobile Number'}</span>
                                <span className="info-value">+91 {user.mobile}</span>
                            </div>

                            <div className="profile-info-card">
                                <span className="info-label">{isHindi ? '🪪 पैन नंबर' : '🪪 PAN Number'}</span>
                                <span className="info-value">{user.panNumber || '--'}</span>
                            </div>
                            <div className="profile-info-card">
                                <span className="info-label">{isHindi ? '🎂 जन्म तिथि' : '🎂 Date of Birth'}</span>
                                <span className="info-value">{formatDobDisplayValue(user.dob)}</span>
                            </div>
                            <div className="profile-info-card">
                                <span className="info-label">{isHindi ? '🆔 आधार (आखिरी 4)' : '🆔 Aadhaar (Last 4)'}</span>
                                <span className="info-value">{getAadhaarDisplayValue(user.aadhaarMaskedUid, user.aadhaarLast4)}</span>
                            </div>
                            <div className="profile-info-card">
                                <span className="info-label">{isHindi ? '📍 पता' : '📍 Address'}</span>
                                <span className="info-value">{formatAddressDisplayValue(user.panAddress)}</span>
                            </div>
                        </div>

                        {/* Stats Row */}
                        <div className="profile-stats-row">
                            <div className="profile-stat">
                                <div className="profile-stat-value" style={{ color: creditScoreMeta.color }}>{creditScoreMeta.value}</div>
                                <div className="profile-stat-label">{isHindi ? 'सिबिल स्कोर' : 'CIBIL Score'}</div>
                                <div className="profile-stat-sub">{creditScoreMeta.label}</div>
                            </div>
                            <div className="profile-stat-divider" />
                            <div className="profile-stat">
                                <div className="profile-stat-value" style={{ color: '#ea580c' }}>
                                    {`₹${displayedJourneyEntryAmount.toLocaleString('en-IN')}`}
                                </div>
                                <div className="profile-stat-label">{isHindi ? 'ऑफर पूर्वावलोकन' : 'Offer Preview'}</div>
                                <div className="profile-stat-sub">{isHindi ? 'AA के बाद फाइनल' : 'Final after AA'}</div>
                            </div>
                            <div className="profile-stat-divider" />
                            <div className="profile-stat">
                                <div className="profile-stat-value" style={{ color: '#3b82f6' }}>{`₹${displayedJourneyEntryAmount.toLocaleString('en-IN')}`}</div>
                                <div className="profile-stat-label">{isHindi ? 'अधिकतम ऋण सीमा' : 'Max Loan Limit'}</div>
                                <div className="profile-stat-sub">{isHindi ? 'वास्तविक ऑफर नहीं' : 'Not the live offer'}</div>
                            </div>
                        </div>
                    </div>
                );
            case 'settings':
                return (
                    <div className="settings-page">
                        <section className="settings-hero">
                            <div className="settings-hero-copy">
                                <p className="settings-kicker">{isHindi ? 'सिस्टम प्राथमिकताएँ' : 'System Preferences'}</p>
                                <h2>{isHindi ? 'सेटिंग्स और सुरक्षा' : 'Settings & Security'}</h2>
                                <p>{isHindi ? 'इस कियोस्क-सहायता प्राप्त खाते के लिए सेशन नियंत्रण, सेवा स्थिति और सहायता विवरण देखें।' : 'Review session controls, service status, and support details for this kiosk-assisted account.'}</p>
                            </div>
                            <div className="settings-version-card">
                                <span>{isHindi ? 'एप्लिकेशन संस्करण' : 'Application Version'}</span>
                                <strong>v1.0.0</strong>
                                <small>{isHindi ? 'नवीनतम डिप्लॉय किया गया बिल्ड' : 'Latest deployed build'}</small>
                            </div>
                        </section>

                        <section className="settings-grid">
                            <div className="settings-card settings-card-primary">
                                <div className="settings-card-head">
                                    <div>
                                        <p className="settings-card-kicker">{isHindi ? 'खाता नियंत्रण' : 'Account Control'}</p>
                                        <h3>{isHindi ? 'सुरक्षा अवलोकन' : 'Security Overview'}</h3>
                                    </div>
                                    <span className="settings-status-pill">{isHindi ? 'सुरक्षित' : 'Protected'}</span>
                                </div>
                                <div className="settings-list">
                                    <div className="settings-row">
                                        <div>
                                            <strong>{isHindi ? 'ओटीपी प्रमाणीकरण' : 'OTP Authentication'}</strong>
                                            <span>{isHindi ? 'लॉगिन एक्सेस मोबाइल ओटीपी से सत्यापित होता है।' : 'Login access is validated using mobile OTP.'}</span>
                                        </div>
                                        <b>{isHindi ? 'सक्षम' : 'Enabled'}</b>
                                    </div>
                                    <div className="settings-row">
                                        <div>
                                            <strong>{isHindi ? 'सेशन बाइंडिंग' : 'Session Binding'}</strong>
                                            <span>{isHindi ? 'प्रोफाइल एक्सेस से पहले मोबाइल, पहचान और ब्यूरो मिलान जाँचा जाता है।' : 'Mobile, identity, and bureau matching are checked before profile access.'}</span>
                                        </div>
                                        <b>{isHindi ? 'सक्रिय' : 'Active'}</b>
                                    </div>
                                    <div className="settings-row">
                                        <div>
                                            <strong>{isHindi ? 'केवाईसी निरंतरता' : 'KYC Continuation'}</strong>
                                            <span>{isHindi ? 'आपका डैशबोर्ड नवीनतम सत्यापित चरण से फिर शुरू होता है।' : 'Your dashboard resumes from the latest verified stage.'}</span>
                                        </div>
                                        <b>{isHindi ? 'तैयार' : 'Ready'}</b>
                                    </div>
                                </div>
                            </div>

                            <div className="settings-card">
                                <div className="settings-card-head">
                                    <div>
                                        <p className="settings-card-kicker">{isHindi ? 'प्राथमिकताएँ' : 'Preferences'}</p>
                                        <h3>{isHindi ? 'सामान्य सेटिंग्स' : 'General Settings'}</h3>
                                    </div>
                                </div>
                                <div className="settings-list">
                                    <div className="settings-row">
                                        <div>
                                            <strong>{isHindi ? 'भाषा' : 'Language'}</strong>
                                            <span>{isHindi ? 'कियोस्क वर्कफ़्लो की वर्तमान डिस्प्ले भाषा।' : 'Current display language for the kiosk workflow.'}</span>
                                        </div>
                                        <b>{isHindi ? 'हिंदी' : 'English'}</b>
                                    </div>
                                    <div className="settings-row">
                                        <div>
                                            <strong>{isHindi ? 'सूचनाएँ' : 'Notifications'}</strong>
                                            <span>{isHindi ? 'जर्नी प्रॉम्प्ट और पुनर्भुगतान रिमाइंडर दिखाई देते रहेंगे।' : 'Journey prompts and repayment reminders remain visible.'}</span>
                                        </div>
                                        <b>{isHindi ? 'सक्षम' : 'Enabled'}</b>
                                    </div>
                                    <div className="settings-row">
                                        <div>
                                            <strong>{isHindi ? 'सहायता चैनल' : 'Support Channel'}</strong>
                                            <span>{isHindi ? 'कियोस्क सपोर्ट एस्केलेशन के माध्यम से सहायता उपलब्ध है।' : 'Assistance is available through kiosk support escalation.'}</span>
                                        </div>
                                        <b>{isHindi ? 'सक्रिय' : 'Live'}</b>
                                    </div>
                                </div>
                            </div>

                            <div className="settings-card">
                                <div className="settings-card-head">
                                    <div>
                                        <p className="settings-card-kicker">{isHindi ? 'सेवा विवरण' : 'Service Details'}</p>
                                        <h3>{isHindi ? 'संस्था और सहायता' : 'Institution & Help'}</h3>
                                    </div>
                                </div>
                                <div className="settings-service-grid">
                                    <div className="settings-service-item">
                                        <span>{isHindi ? 'पंजीकृत एनबीएफसी' : 'Registered NBFC'}</span>
                                        <strong>Pawansut Holding Limited</strong>
                                    </div>
                                    <div className="settings-service-item">
                                        <span>{isHindi ? 'सहायता नंबर' : 'Support Number'}</span>
                                        <strong>1800-XXX-XXXX</strong>
                                    </div>
                                    <div className="settings-service-item">
                                        <span>{isHindi ? 'पुनर्भुगतान मोड' : 'Repayment Mode'}</span>
                                        <strong>PayU Hosted Checkout</strong>
                                    </div>
                                    <div className="settings-service-item">
                                        <span>{isHindi ? 'सत्यापन मोड' : 'Verification Mode'}</span>
                                        <strong>OTP + PAN + KYC</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="settings-card settings-signout-card">
                                <div className="settings-signout-actions">
                                    <button className="settings-logout-btn settings-logout-btn-primary" onClick={handleLogout}>{isHindi ? 'लॉगआउट' : 'Logout'}</button>
                                    {kioskRuntime.isElectron && (
                                        <button
                                            className="settings-logout-btn"
                                            onClick={handleKioskExit}
                                            style={{
                                                background: kioskRuntime.adminExitConfigured ? '#1e293b' : '#64748b',
                                                color: '#fff'
                                            }}
                                        >
                                            {kioskRuntime.adminExitConfigured
                                                ? (isHindi ? 'कियोस्क से बाहर निकलें' : 'Exit Kiosk')
                                                : (isHindi ? 'कियोस्क एक्ज़िट सेटअप' : 'Exit Kiosk Setup')}
                                        </button>
                                    )}
                                </div>
                                <div className="settings-signout-text">
                                    <p className="settings-card-kicker">{isHindi ? 'सेशन समाप्ति' : 'Session Exit'}</p>
                                    <h3>{isHindi ? 'साइन आउट' : 'Sign Out'}</h3>
                                    <p className="settings-signout-copy">{isHindi ? 'जब आप खाता समीक्षा पूरी कर लें, तब इस कियोस्क सेशन को सुरक्षित रूप से समाप्त करें।' : 'End this kiosk session securely when you are done reviewing your account.'}</p>
                                </div>
                            </div>
                        </section>
                    </div>
                );
            default:
                return null;
        }
    };

    if (entryLoaderMessage) return <FullPageLoader message={entryLoaderMessage} />;

    return (
        <div className={`kiosk-theme db-page dashboard-wrapper ${isSidebarExpanded ? 'sidebar-expanded' : ''}`}>
            {/* Confetti / Flower Effect */}
            {confetti.length > 0 && (
                <div className="confetti-container">
                    {confetti.map(p => (
                        <div
                            key={p.id}
                            className="confetti-piece"
                            style={{
                                left: `${p.left}%`,
                                backgroundColor: p.color,
                                animationDelay: `${p.delay}s`,
                                animationDuration: `${p.duration}s`,
                                transform: `rotate(${p.rotate}deg)`
                            }}
                        />
                    ))}
                </div>
            )}

            <SalarySlipUploadModal
                open={showSalarySlipModal}
                mobile={String(user.mobile || '')}
                isHindi={isHindi}
                onClose={() => setShowSalarySlipModal(false)}
                onUploaded={(status) => {
                    setSalarySlipStatus(status);
                    if (status?.status === 'VERIFIED') refreshDashboardUserData();
                }}
            />
            {/* Congratulations Modal */}
            {showCongrats && (
                <div className="popup-overlay">
                    <div className="popup-box congrats-modal">
                        <div className="congrats-emoji">👏</div>
                        <h2 className="congrats-title">
                            Congratulations, {(user.name && user.name.toLowerCase() !== 'new user') ? localizedFirstName : 'Customer'}!
                        </h2>
                        <p style={{ fontSize: '1.2rem', margin: '10px 0' }}>We have something special for you.</p>
                        <div style={{ padding: '20px', background: '#f3f4f6', borderRadius: '20px', border: '1px dashed #9ca3af', margin: '20px 0' }}>
                            <span style={{ color: '#6b7280', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                Approved Offer
                            </span>
                            <span className="congrats-amount">
                                {`Up to ₹${displayedCongratsAmount.toLocaleString('en-IN')}`}
                            </span>
                            <p style={{ color: '#374151', fontWeight: '700', margin: 0 }}>
                                ✓ Pending disbursement after journey completion
                            </p>
                        </div>
                        <button className="celebrate-btn" onClick={dismissCongratsPopup}>
                            Awesome! Check My Offer →
                        </button>
                    </div>
                </div>
            )}

            {showLogoutConfirm && (
                <div className="popup-overlay">
                    <div className="popup-box" style={{ paddingTop: '28px' }}>
                        <h3 style={{ margin: '0 0 10px', color: '#1e293b' }}>{isHindi ? 'लॉगआउट की पुष्टि करें' : 'Confirm Logout'}</h3>
                        <p style={{ marginTop: 0 }}>{isHindi ? 'क्या आप इस सेशन से लॉगआउट करना चाहते हैं?' : 'Do you want to log out from this session?'}</p>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '18px' }}>
                            <button
                                type="button"
                                onClick={() => setShowLogoutConfirm(false)}
                                style={{ flex: 1, background: '#cb7b18ff', color: '#1e293b', border: 'none', borderRadius: '12px', padding: '12px 16px', fontWeight: 700, cursor: 'pointer' }}
                            >
                                {isHindi ? 'रद्द करें' : 'Cancel'}
                            </button>
                            <button
                                type="button"
                                onClick={confirmLogout}
                                style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', borderRadius: '12px', padding: '12px 16px', fontWeight: 700, cursor: 'pointer' }}
                            >
                                {isHindi ? 'लॉगआउट' : 'Logout'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSupportModal && (
                <div className="popup-overlay">
                    <div className="popup-box support-modal-box">
                        <div className="support-modal-header">
                            <div>
                                <h3>Help & Support</h3>
                                <p>Fill in all mandatory fields and send your request.</p>
                            </div>
                        </div>
                        <SupportRequestForm
                            embedded
                            onCancel={() => setShowSupportModal(false)}
                            onSuccess={handleSupportSuccess}
                        />
                    </div>
                </div>
            )}
            {repaymentLockModal.show && (
                <div className="popup-overlay" onClick={() => setRepaymentLockModal({ show: false, message: '' })}>
                    <div className="popup-box" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
                        <div className="popup-icon">i</div>
                        <h3>{isHindi ? 'भुगतान अभी उपलब्ध नहीं' : 'Payment Not Available Yet'}</h3>
                        <p>{repaymentLockModal.message}</p>
                        <button
                            type="button"
                            onClick={() => setRepaymentLockModal({ show: false, message: '' })}
                        >
                            {isHindi ? 'ठीक है' : 'Okay'}
                        </button>
                    </div>
                </div>
            )}

            {showSalaryHintModal && (
                <div
                    className="popup-overlay"
                    style={{
                        zIndex: 10,
                        alignItems: 'flex-start',
                        paddingTop: 'max(12px, env(safe-area-inset-top))',
                        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                        overflowY: 'auto',
                        pointerEvents: 'auto'
                    }}
                >
                    <div
                        className="popup-box"
                        ref={salaryHintModalRef}
                        style={{
                            position: 'relative',
                            zIndex: 11,
                            maxWidth: '680px',
                            width: 'calc(100vw - 28px)',
                            padding: '26px 28px',
                            maxHeight: 'calc(100dvh - 24px)',
                            overflowY: 'auto',
                            margin: '0 auto',
                            pointerEvents: 'auto'
                        }}
                    >
                        <h3 style={{ marginTop: 0, marginBottom: '6px', color: '#0f172a' }}>
                            {isHindi ? 'कंपनी, सैलरी और रेफरेंस विवरण' : 'Company, Salary & Reference Details'}
                        </h3>
                        <p style={{ marginTop: 0, marginBottom: '16px', color: '#475569', fontSize: '0.9rem' }}>
                            {isHindi
                                ? 'लोन प्रक्रिया आगे बढ़ाने के लिए कृपया अपनी कंपनी, सैलरी ब्रैकेट और 2 रेफरेंस संपर्क भरें।'
                                : 'Please provide company, salary bracket, and 2 reference contacts to proceed with your loan.'}
                        </p>
                        <div style={{ display: 'grid', gap: '12px' }}>
                            <label
                                style={{
                                    display: 'grid',
                                    gap: '6px',
                                    textAlign: 'left',
                                    marginBottom: showCompanySuggestions ? '188px' : '0'
                                }}
                            >
                                <span style={{ fontWeight: 600, color: '#1e293b' }}>{isHindi ? 'कंपनी नाम' : 'Company Name'}</span>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        ref={companyInputRef}
                                        value={companySearchQuery || selectedCompanyName || ''}
                                        onChange={(event) => {
                                            const next = event.target.value;
                                            setCompanySearchQuery(next);
                                            setSelectedCompanyName(next);
                                            setShowCompanySuggestions(true);
                                        }}
                                        onInput={(event) => {
                                            const next = event.target.value;
                                            setCompanySearchQuery(next);
                                            setSelectedCompanyName(next);
                                        }}
                                        onFocus={() => setShowCompanySuggestions(true)}
                                        onBlur={() => {
                                            window.setTimeout(() => {
                                                setShowCompanySuggestions(false);
                                            }, 120);
                                        }}
                                        placeholder={isHindi ? 'कंपनी नाम लिखें' : 'Type company name'}
                                        style={{ width: '100%', minHeight: '46px', borderRadius: '10px', border: '1px solid #cbd5e1', padding: '0 12px', boxSizing: 'border-box', color: '#0f172a', caretColor: '#0f172a' }}
                                    />
                                    {showCompanySuggestions && (
                                        <div style={{ position: 'absolute', top: '50px', left: 0, right: 0, maxHeight: '180px', overflowY: 'auto', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '10px', boxShadow: '0 14px 30px rgba(15,23,42,0.14)', zIndex: 5 }}>
                                            {COMPANY_NAME_OPTIONS
                                                .filter((name) => name.toLowerCase().includes(String(companySearchQuery || '').toLowerCase()))
                                                .slice(0, 8)
                                                .map((name) => (
                                                    <button
                                                        key={name}
                                                        type="button"
                                                        onMouseDown={(event) => {
                                                            event.preventDefault();
                                                            handleCompanySuggestionSelect(name);
                                                        }}
                                                        onTouchStart={(event) => {
                                                            event.preventDefault();
                                                            handleCompanySuggestionSelect(name);
                                                        }}
                                                        onClick={() => handleCompanySuggestionSelect(name)}
                                                        style={{ width: '100%', textAlign: 'left', background: '#fff', border: 'none', borderBottom: '1px solid #e2e8f0', padding: '10px 12px', cursor: 'pointer' }}
                                                    >
                                                        {name}
                                                    </button>
                                                ))}
                                            {!COMPANY_NAME_OPTIONS.some((name) => name.toLowerCase().includes(String(companySearchQuery || '').toLowerCase())) && (
                                                <div style={{ padding: '10px 12px', color: '#64748b', fontSize: '0.9rem' }}>
                                                    {isHindi ? 'कोई सुझाव नहीं मिला, आप टाइप किया हुआ नाम वैसे ही जारी रख सकते हैं।' : 'No suggestions found. You can continue with the typed company name.'}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </label>
                            <label style={{ display: 'grid', gap: '6px', textAlign: 'left' }}>
                                <span style={{ fontWeight: 600, color: '#1e293b' }}>{isHindi ? 'सैलरी ब्रैकेट' : 'Salary Bracket'}</span>
                                <select
                                    value={selectedSalaryBracket}
                                    onChange={(event) => setSelectedSalaryBracket(event.target.value)}
                                    style={{ minHeight: '44px', borderRadius: '10px', border: '1px solid #cbd5e1', padding: '0 12px' }}
                                >
                                    <option value="">{isHindi ? 'चुनें' : 'Select'}</option>
                                    {SALARY_BRACKET_OPTIONS.map((item) => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </select>
                            </label>

                            {/* Reference 1 Section */}
                            <div style={{ marginTop: '6px', padding: '14px 16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'left' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.08em', color: '#ea580c', textTransform: 'uppercase', background: '#ffedd5', padding: '3px 8px', borderRadius: '6px' }}>
                                        {isHindi ? 'रेफरेंस 1' : 'Reference 1'}
                                    </span>
                                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                                        {isHindi ? '(पारिवारिक / करीबी संपर्क)' : '(Family / Close Contact)'}
                                    </span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                                    <label style={{ display: 'grid', gap: '4px' }}>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{isHindi ? 'पूरा नाम' : 'Full Name'}</span>
                                        <input
                                            type="text"
                                            value={reference1.name}
                                            onChange={(e) => setReference1((prev) => ({ ...prev, name: e.target.value }))}
                                            placeholder={isHindi ? 'उदा. रमेश शर्मा' : 'e.g. Ramesh Sharma'}
                                            style={{ minHeight: '42px', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '0 10px', fontSize: '0.92rem', color: '#0f172a' }}
                                        />
                                    </label>
                                    <label style={{ display: 'grid', gap: '4px' }}>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{isHindi ? '10-अंकों का मोबाइल नंबर' : '10-Digit Mobile Number'}</span>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            maxLength={10}
                                            value={reference1.mobile}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                setReference1((prev) => ({ ...prev, mobile: val }));
                                            }}
                                            placeholder="9876543210"
                                            style={{ minHeight: '42px', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '0 10px', fontSize: '0.92rem', color: '#0f172a' }}
                                        />
                                    </label>
                                    <label style={{ display: 'grid', gap: '4px' }}>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{isHindi ? 'संबंध' : 'Relation'}</span>
                                        <select
                                            value={reference1.relation}
                                            onChange={(e) => setReference1((prev) => ({ ...prev, relation: e.target.value }))}
                                            style={{ minHeight: '42px', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '0 10px', fontSize: '0.92rem', color: '#0f172a', background: '#fff' }}
                                        >
                                            {REFERENCE_RELATION_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{isHindi ? opt.labelHi : opt.labelEn}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>

                            {/* Reference 2 Section */}
                            <div style={{ marginTop: '2px', padding: '14px 16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'left' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '0.08em', color: '#0284c7', textTransform: 'uppercase', background: '#e0f2fe', padding: '3px 8px', borderRadius: '6px' }}>
                                        {isHindi ? 'रेफरेंस 2' : 'Reference 2'}
                                    </span>
                                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                                        {isHindi ? '(मित्र / सहकर्मी / अन्य)' : '(Friend / Colleague / Other)'}
                                    </span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                                    <label style={{ display: 'grid', gap: '4px' }}>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{isHindi ? 'पूरा नाम' : 'Full Name'}</span>
                                        <input
                                            type="text"
                                            value={reference2.name}
                                            onChange={(e) => setReference2((prev) => ({ ...prev, name: e.target.value }))}
                                            placeholder={isHindi ? 'उदा. सुनीता देवी' : 'e.g. Sunita Devi'}
                                            style={{ minHeight: '42px', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '0 10px', fontSize: '0.92rem', color: '#0f172a' }}
                                        />
                                    </label>
                                    <label style={{ display: 'grid', gap: '4px' }}>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{isHindi ? '10-अंकों का मोबाइल नंबर' : '10-Digit Mobile Number'}</span>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            maxLength={10}
                                            value={reference2.mobile}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                setReference2((prev) => ({ ...prev, mobile: val }));
                                            }}
                                            placeholder="9123456780"
                                            style={{ minHeight: '42px', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '0 10px', fontSize: '0.92rem', color: '#0f172a' }}
                                        />
                                    </label>
                                    <label style={{ display: 'grid', gap: '4px' }}>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>{isHindi ? 'संबंध' : 'Relation'}</span>
                                        <select
                                            value={reference2.relation}
                                            onChange={(e) => setReference2((prev) => ({ ...prev, relation: e.target.value }))}
                                            style={{ minHeight: '42px', borderRadius: '8px', border: '1px solid #cbd5e1', padding: '0 10px', fontSize: '0.92rem', color: '#0f172a', background: '#fff' }}
                                        >
                                            {REFERENCE_RELATION_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{isHindi ? opt.labelHi : opt.labelEn}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                            <button
                                type="button"
                                onClick={() => setShowSalaryHintModal(false)}
                                style={{ flex: 1, background: '#e2e8f0', color: '#0f172a', border: 'none', borderRadius: '10px', padding: '11px 12px', fontWeight: 700 }}
                            >
                                {isHindi ? 'रद्द करें' : 'Cancel'}
                            </button>
                            <button
                                type="button"
                                onClick={handleProceedLoanJourneyWithSalaryHint}
                                style={{ flex: 1, background: '#ea580c', color: '#fff', border: 'none', borderRadius: '10px', padding: '11px 12px', fontWeight: 700 }}
                            >
                                {isHindi ? 'जारी रखें' : 'Continue'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showLowSalaryIneligibleModal && (
                <div className="popup-overlay">
                    <div
                        className="popup-box"
                        onClick={(event) => event.stopPropagation()}
                        style={{ maxWidth: '860px', width: 'calc(100vw - 80px)', minHeight: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '48px 56px' }}
                    >
                        <p style={{ margin: '6px 0', color: '#475569', lineHeight: 1.6, fontWeight: 600 }}>
                            {isHindi
                                ? 'आपकी सैलरी 20,000 रुपये से कम है, इसलिए हम आपको लोन नहीं दे सकते।'
                                : 'Your salary is below Rs. 20,000, so we are unable to offer you a loan.'}
                        </p>
                    </div>
                </div>
            )}

            {showSanctionLetterPreviewModal && (
                <div className="popup-overlay" onClick={() => setShowSanctionLetterPreviewModal(false)}>
                    <div
                        className="popup-box"
                        style={{
                            width: 'min(980px, calc(100vw - 32px))',
                            maxHeight: 'calc(100vh - 32px)',
                            overflow: 'hidden',
                            padding: '0'
                        }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #e2e8f0', background: '#fffaf5' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#111827' }}>Sanction Letter Preview</h3>
                                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.92rem' }}>
                                    {latestSanctionLetter?.loanId ? `${latestSanctionLetter.loanId}` : 'Live sanction letter'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowSanctionLetterPreviewModal(false)}
                                aria-label="Close sanction letter preview"
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: '1.6rem',
                                    lineHeight: 1,
                                    cursor: 'pointer',
                                    color: '#111827'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ padding: '16px', background: '#f8fafc' }}>
                            <div
                                style={{
                                    width: '100%',
                                    height: 'calc(100vh - 180px)',
                                    minHeight: '680px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '18px',
                                    overflow: 'auto',
                                    background: '#fff'
                                }}
                            >
                                {sanctionLetterPreviewUrl ? (
                                    <iframe
                                        title="Sanction Letter Preview Modal"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: '0',
                                            background: '#fff'
                                        }}
                                        src={sanctionLetterPreviewUrl}
                                    />
                                ) : resolvedSanctionLetterPreviewDocument ? (
                                    <iframe
                                        title="Sanction Letter Preview Modal"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: '0',
                                            background: '#fff'
                                        }}
                                        srcDoc={resolvedSanctionLetterPreviewDocument || undefined}
                                    />
                                ) : sanctionLetterPreviewText ? (
                                    <pre style={{
                                        margin: 0,
                                        padding: '24px',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontFamily: 'Georgia, "Times New Roman", serif',
                                        fontSize: '1rem',
                                        lineHeight: 1.75,
                                        color: '#0f172a'
                                    }}>
                                        {sanctionLetterPreviewText}
                                    </pre>
                                ) : (
                                    <div style={{ padding: '24px', color: '#64748b' }}>
                                        Loading preview…
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {supportToast.show && (
                <div className="support-toast" role="status" aria-live="polite">
                    {supportToast.message}
                </div>
            )}

            {/* Floating Sidebar */}
            <nav className={`sidebar ${isSidebarExpanded ? 'expanded' : ''}`}>
                <button
                    type="button"
                    className="sidebar-toggle"
                    onClick={() => setIsSidebarExpanded((prev) => !prev)}
                    aria-pressed={isSidebarExpanded}
                    aria-label={isSidebarExpanded
                        ? (isHindi ? 'साइडबार छोटा करें' : 'Collapse sidebar')
                        : (isHindi ? 'साइडबार फैलाएं' : 'Expand sidebar')}
                    title={isSidebarExpanded
                        ? (isHindi ? 'साइडबार छोटा करें' : 'Collapse sidebar')
                        : (isHindi ? 'साइडबार फैलाएं' : 'Expand sidebar')}
                >
                    <UiIcon name={isSidebarExpanded ? 'chevron-left' : 'chevron-right'} />
                </button>
                {sidebarItems.map((item) => (
                    <div
                        key={item.key}
                        className={`sidebar-icon ${activeTab === item.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(item.key)}
                        title={item.label}
                    >
                        <UiIcon name={item.icon} />
                        <span className="sidebar-label">{item.label}</span>
                    </div>
                ))}
                <div className="sidebar-icon logout" onClick={handleLogout} title={isHindi ? 'लॉग आउट' : 'Log Out'}>
                    <UiIcon name="logout" />
                    <span className="sidebar-label">{isHindi ? 'लॉग आउट' : 'Log Out'}</span>
                </div>
            </nav>

            <div className="dashboard-container">
                {/* Header */}
                <header className="dashboard-header">
                    <div className="user-welcome">
                        <h2>Welcome back, {localizedUserName}!</h2>
                        <p>Financial freedom is just a click away.</p>
                    </div>
                </header>

                {showDashboardBanner && (
                    <section className="dashboard-team-banner" aria-label="FinMudra team achievement banner">
                        <img
                            src="/dashboard-banner.jpg"
                            alt="Congratulations to the FinMudra team for disbursing Rs 13.37 lakhs in a single day"
                            loading="eager"
                            onError={() => setShowDashboardBanner(false)}
                        />
                    </section>
                )}

                {/* Dynamic Content */}
                {renderContent()}
            </div>
        </div>
    );
}

export default Dashboard;
